import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import { applyImageFallback, documentThumbnailUrl } from "../utils/documentPreviews";
import PreviewModal from "./PreviewModal";
import "./ResearchPage.css";

const encodeDocumentPath = (path) => path.split("/").map((segment) => encodeURIComponent(segment)).join("/");

const normalizeSearchText = (parts) => parts.filter(Boolean).join(" ").toLowerCase();
const newsletterCategories = ["Article", "Advert", "Calendar", "Charity", "Classifieds", "Contact / Directory", "Events", "Editorial", "Humour", "Instruction", "Profile", "Province", "Quiz", "Reference", "Reader section", "Side degrees"];
const PUBLIC_RESEARCH_PAPER_DOCUMENT_ID = "a415b62c-f386-467a-a76f-f980feefebeb";
const documentDownloadUrl = (documentId, fallbackUrl = "") => (
    documentId ? `/api/documents/${encodeURIComponent(documentId)}/download` : fallbackUrl
);
const advertTypes = ["Charity event", "General advert", "Meeting notice", "Fundraising", "Social event", "Masonic service", "Other advert"];

const formatSurveyPercent = (count, total) => {
    if (!total) return "0%";
    return `${Math.round((Number(count || 0) / Number(total || 0)) * 100)}%`;
};

const formatResearchDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toISOString().slice(0, 10);
};

const formatTimelineDateTag = (value, fallbackYear = "", displayMode = "auto") => {
    const formatted = formatResearchDate(value);
    if (displayMode === "year") return formatted?.slice(0, 4) || fallbackYear || "Undated";
    if (displayMode === "exact") return formatted || fallbackYear || "Undated";
    if (/^\d{4}-01-01$/.test(formatted)) return formatted.slice(0, 4);
    return formatted || fallbackYear || "Undated";
};

const formatResearchDateRange = (first, last) => {
    const start = formatResearchDate(first);
    const end = formatResearchDate(last);
    if (start && end && start !== end) return `${start} to ${end}`;
    return start || end || "Undated";
};

const formatResearchCurrency = (cents) => {
    const value = Number(cents || 0) / 100;
    return `R ${value.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const researchDateValue = (value) => {
    const formatted = formatResearchDate(value);
    return /^\d{4}-\d{2}-\d{2}$/.test(formatted) ? formatted : "";
};

const dateInResearchRange = (value, from, to) => {
    if (!from && !to) return true;
    const date = researchDateValue(value);
    if (!date) return false;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
};

const dateRangeIntersectsResearchRange = (first, last, from, to) => {
    if (!from && !to) return true;
    const start = researchDateValue(first);
    const end = researchDateValue(last) || start;
    if (!start && !end) return false;
    if (from && end && end < from) return false;
    if (to && start && start > to) return false;
    return true;
};

const compareResearchDateValues = (a, b, direction = "newest") => {
    const dateA = researchDateValue(a);
    const dateB = researchDateValue(b);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return direction === "oldest" ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
};

const researchDatePresetRange = (preset) => {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    const start = new Date(now);
    if (preset === "year") {
        start.setFullYear(start.getFullYear() - 1);
    } else if (preset === "quarter") {
        start.setMonth(start.getMonth() - 3);
    } else if (preset === "month") {
        start.setDate(start.getDate() - 30);
    } else if (preset === "this-year") {
        return { from: `${now.getFullYear()}-01-01`, to: today };
    } else {
        return { from: "", to: "" };
    }
    return { from: start.toISOString().slice(0, 10), to: today };
};

const surveyRows = (counts = {}) =>
    Object.entries(counts)
        .map(([label, count]) => ({ label, count: Number(count || 0) }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

const getSurveyQuestion = (results, id) => (results?.questions || []).find((question) => question.id === id);

const surveyBarOption = (question, title) => {
    const rows = surveyRows(question?.counts).slice(0, 8).reverse();
    return {
        title: { text: title || question?.label || "", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { top: 42, right: 20, bottom: 20, left: 140 },
        xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#64748b" } },
        yAxis: { type: "category", data: rows.map((row) => row.label), axisLabel: { color: "#475569", width: 130, overflow: "truncate" } },
        series: [{ type: "bar", data: rows.map((row) => row.count), itemStyle: { color: "#2f6c45", borderRadius: [0, 5, 5, 0] }, label: { show: true, position: "right" } }]
    };
};

const surveyDonutOption = (question, title) => ({
    title: { text: title || question?.label || "", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
    tooltip: { trigger: "item" },
    legend: { bottom: 0, type: "scroll", textStyle: { color: "#475569" } },
    series: [{
        type: "pie",
        radius: ["42%", "68%"],
        center: ["50%", "46%"],
        data: surveyRows(question?.counts).map((row) => ({ name: row.label, value: row.count })),
        label: { formatter: "{b}: {c}" },
        itemStyle: { borderColor: "#fff", borderWidth: 2 }
    }]
});

const surveyMatrixOption = (question) => {
    const scale = ["Not at all", "Slightly", "Moderately", "Mostly", "Completely"];
    const prompts = Object.keys(question?.matrix || {});
    return {
        title: { text: "Experience Rating Profile", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: { top: 26, textStyle: { color: "#475569" } },
        grid: { top: 74, right: 20, bottom: 16, left: 190 },
        xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#64748b" } },
        yAxis: { type: "category", data: prompts, axisLabel: { color: "#475569", width: 180, overflow: "truncate" } },
        series: scale.map((label) => ({
            name: label,
            type: "bar",
            stack: "rating",
            data: prompts.map((prompt) => Number(question?.matrix?.[prompt]?.[label] || 0))
        }))
    };
};

function ResearchPage({ user, openLoginModal }) {
    const navigate = useNavigate();
    const selectedNewsletterDetailRef = useRef(null);
    const pendingNewsletterEntryScrollRef = useRef(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [newsletterSearch, setNewsletterSearch] = useState("");
    const [newsletterCategory, setNewsletterCategory] = useState("");
    const [newsletterIssues, setNewsletterIssues] = useState([]);
    const [newsletterLoading, setNewsletterLoading] = useState(false);
    const [newsletterLoaded, setNewsletterLoaded] = useState(false);
    const [newsletterError, setNewsletterError] = useState("");
    const [newsletterRefreshing, setNewsletterRefreshing] = useState(false);
    const [selectedNewsletterId, setSelectedNewsletterId] = useState("");
    const [selectedNewsletterSection, setSelectedNewsletterSection] = useState(null);
    const [newsletterDateFrom, setNewsletterDateFrom] = useState("");
    const [newsletterDateTo, setNewsletterDateTo] = useState("");
    const [newsletterSort, setNewsletterSort] = useState("newest");
    const [newsletterIndexMeta, setNewsletterIndexMeta] = useState(null);
    const [newsletterEditor, setNewsletterEditor] = useState(null);
    const [newsletterSaving, setNewsletterSaving] = useState(false);
    const [newsletterEditorError, setNewsletterEditorError] = useState("");
    const [surveyResults] = useState(null);
    const surveyLoading = false;
    const surveyError = "";
    const [lodgeHeritage, setLodgeHeritage] = useState(null);
    const [lodgeHeritageLoading, setLodgeHeritageLoading] = useState(false);
    const [lodgeHeritageError, setLodgeHeritageError] = useState("");
    const [lodgeHeritageSearch, setLodgeHeritageSearch] = useState("");
    const [lodgeHeritageOrder, setLodgeHeritageOrder] = useState("all");
    const [lodgeHeritageCategory, setLodgeHeritageCategory] = useState("all");
    const [selectedLodgeHeritageId, setSelectedLodgeHeritageId] = useState("");
    const [charityFundraising, setCharityFundraising] = useState(null);
    const [charityFundraisingLoading, setCharityFundraisingLoading] = useState(false);
    const [charityFundraisingError, setCharityFundraisingError] = useState("");
    const [charityFundraisingSearch, setCharityFundraisingSearch] = useState("");
    const [charityFundraisingLodge, setCharityFundraisingLodge] = useState("");
    const [charityFundraisingCategory, setCharityFundraisingCategory] = useState("all");
    const [charityFundraisingDateFrom, setCharityFundraisingDateFrom] = useState("");
    const [charityFundraisingDateTo, setCharityFundraisingDateTo] = useState("");
    const [charityFundraisingSort, setCharityFundraisingSort] = useState("newest");
    const [charityCollapsedSections, setCharityCollapsedSections] = useState({
        analytics: false,
        crossrefs: false,
        records: true,
        sources: false,
    });
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [publicCollections, setPublicCollections] = useState({});
    const [publicCollectionSearch, setPublicCollectionSearch] = useState("");
    const [publicCollectionCategory, setPublicCollectionCategory] = useState("");
    const [publicCollectionDateFrom, setPublicCollectionDateFrom] = useState("");
    const [publicCollectionDateTo, setPublicCollectionDateTo] = useState("");
    const [publicCollectionSort, setPublicCollectionSort] = useState("newest");
    const [publicCollectionSaving, setPublicCollectionSaving] = useState(false);
    const [publicCollectionError, setPublicCollectionError] = useState("");
    const [historicalHighlights, setHistoricalHighlights] = useState([]);
    const [historicalHighlightsLoading, setHistoricalHighlightsLoading] = useState(false);
    const [historicalHighlightsError, setHistoricalHighlightsError] = useState("");
    const [historicalHighlightsSaving, setHistoricalHighlightsSaving] = useState(false);
    const [expandedHistoricalHighlightId, setExpandedHistoricalHighlightId] = useState("");
    const [selectedInteractiveTimelineId, setSelectedInteractiveTimelineId] = useState("");
    const [interactiveTimelineImportanceFilter, setInteractiveTimelineImportanceFilter] = useState("all");
    const [interactiveTimelineCategoryFilter, setInteractiveTimelineCategoryFilter] = useState("all");
    const [publicationQueue, setPublicationQueue] = useState(null);
    const [publicationQueueLoading, setPublicationQueueLoading] = useState(false);
    const [publicationQueueError, setPublicationQueueError] = useState("");
    const [publicationQueueOpen, setPublicationQueueOpen] = useState(false);
    const [publicationQueueSearch, setPublicationQueueSearch] = useState("");
    const [publicationQueueSavingId, setPublicationQueueSavingId] = useState("");
    const [publicationQueuePreview, setPublicationQueuePreview] = useState({
        open: false,
        title: "",
        content: null,
        type: null,
        error: "",
        downloadUrl: "",
    });
    const [publicImagePreview, setPublicImagePreview] = useState({
        open: false,
        title: "",
        content: null,
        downloadUrl: "",
    });
    const [lodgeHeritageDateFrom, setLodgeHeritageDateFrom] = useState("");
    const [lodgeHeritageDateTo, setLodgeHeritageDateTo] = useState("");
    const [lodgeHeritageSort, setLodgeHeritageSort] = useState("newest");

    const canEditNewsletter = ["admin", "archivist"].includes(String(user?.role || "").toLowerCase());
    const canManageNewsletter = Boolean(newsletterIndexMeta?.canManageNewsletter || canEditNewsletter);
    const canEditPublicCollections = canEditNewsletter;

    const renderResearchDateFilters = ({
        from,
        to,
        sort,
        onFromChange,
        onToChange,
        onSortChange,
        onClear,
        copy = "Filter dated archive entries without changing the public/draft publication state.",
    }) => {
        const applyPreset = (preset) => {
            const range = researchDatePresetRange(preset);
            onFromChange(range.from);
            onToChange(range.to);
        };
        const hasDateFilter = Boolean(from || to);

        return (
            <div className="research-date-filter-panel">
                <div className="research-date-filter-copy">
                    <strong>Date range</strong>
                    <span>{copy}</span>
                </div>
                <div className="research-date-filter-fields">
                    <label>
                        <span>From</span>
                        <input
                            type="date"
                            value={from}
                            onChange={(event) => onFromChange(event.target.value)}
                        />
                    </label>
                    <label>
                        <span>To</span>
                        <input
                            type="date"
                            value={to}
                            onChange={(event) => onToChange(event.target.value)}
                        />
                    </label>
                    <label>
                        <span>Sort</span>
                        <select
                            value={sort}
                            onChange={(event) => onSortChange(event.target.value)}
                        >
                            <option value="newest">Latest first</option>
                            <option value="oldest">Oldest first</option>
                        </select>
                    </label>
                </div>
                <div className="research-date-filter-actions" aria-label="Quick date filters">
                    <button type="button" onClick={() => applyPreset("month")}>30 days</button>
                    <button type="button" onClick={() => applyPreset("quarter")}>3 months</button>
                    <button type="button" onClick={() => applyPreset("this-year")}>This year</button>
                    <button
                        type="button"
                        className={hasDateFilter ? "active" : ""}
                        onClick={onClear}
                    >
                        All dates
                    </button>
                </div>
            </div>
        );
    };

    const researchEntries = [
        {
            title: "Interactive Timeline",
            subtitle: "Browse source-backed people, places, events, and moments from the archive.",
            detail: "The interactive timeline gathers interesting archive moments into a public timeline, with dates, lodges, people, places, photographs, and source notes visible for research context.",
            status: "Source-backed timeline",
            type: "Historical archive",
            icon: "/images/highlights.png",
            contentType: "historical-highlights",
            collectionKey: "historical-highlights",
            cta: "Open Timeline",
        },
        {
            title: "Looking East",
            subtitle: "Browse and search quarterly Looking East issues by contents entry, topic, person, lodge, date, and page.",
            detail: "The first public newsletter index makes Looking East searchable by article, person, lodge, side degree, charity, date, and page. Select a contents heading to read the extracted summary, then open the source PDF at the relevant page.",
            status: "Public index",
            type: "Newsletter index",
            icon: "/images/looking-east-title.png",
            contentType: "looking-east",
            cta: "Open Looking East",
        },
        {
            title: "Connaught 361 Vol. 1 No. 1",
            subtitle: "Read the first public Connaught 361 research paper issue as a PDF.",
            detail: "Published public research paper from the Connaught 361 series.",
            status: "Public PDF",
            type: "Research paper",
            icon: "/images/research.png",
            contentType: "public-research-paper",
            cta: "Open PDF",
            action: () => {
                logResearchAnalytics("research_pdf_open", {
                    collection: "Research Papers",
                    title: "Connaught 361 Vol. 1 No. 1",
                });
                window.open(`/api/public/research-papers/${PUBLIC_RESEARCH_PAPER_DOCUMENT_ID}/pdf`, "_blank", "noopener,noreferrer");
            },
        },
        {
            title: "Research Paper Builder",
            subtitle: "Create, edit, and compile Connaught 361 research papers from the Lodge's LaTeX issue template.",
            detail: "Protected workspace for drafting structured research-paper sections and building publication-ready PDF and TeX outputs.",
            status: user ? "Protected workspace" : "Login required",
            type: "PDF builder",
            icon: "/images/research.png",
            contentType: "research-papers",
            cta: user ? "Open Builder" : "Log in to Open",
            action: () => {
                if (user) {
                    navigate("/research-papers");
                    return;
                }
                if (openLoginModal) openLoginModal();
            },
        },
        {
            title: "Charity & Fundraising",
            subtitle: "Browse charity activity, fundraising events, beneficiaries, lodge links, and source evidence.",
            detail: "This collection combines Resources charity records with archive evidence and matching source documents, creating a public research index across lodges, beneficiaries, events, dates, contribution types, and outcomes.",
            status: "Source-backed index",
            type: "Charity research",
            icon: "/images/charity.svg",
            contentType: "charity-fundraising",
            collectionKey: "charity-fundraising",
            cta: "Open Charity",
            archive: {
                empty: "No public charity or fundraising entries have been indexed from Looking East yet.",
                terms: ["charity", "fundraising", "fundraiser", "donation", "donations", "appeal", "beneficiary", "raffle", "collection", "relief", "community", "service"],
                categories: ["Charity", "Advert"],
            },
            plan: {
                summary: "Charity and fundraising material will be gathered as a research index rather than a single list of events. Each entry should point back to its source document, issue, page, lodge, charity, date, and any recorded amount or outcome.",
                sections: [
                    {
                        title: "Archive signals",
                        items: ["Fundraising appeals and notices", "Charity reports and donation acknowledgements", "Named charities, beneficiaries, and community projects", "Event adverts, raffles, collections, and social fundraising"],
                    },
                    {
                        title: "Future pipe",
                        items: ["Link extracted evidence to Resources charity records", "Chart activity by year, lodge, charity, and project type", "Attach source pages, photos, and supporting documents where public access is appropriate"],
                    },
                ],
            },
        },
        {
            title: "Orders & Lodge Heritage",
            subtitle: "Browse Irish Craft, Royal Arch, Knight Masons, and Knights Templar public lodge heritage.",
            detail: "This collection gathers lodge heritage from the archive in the same pattern as Looking East: public lodge-level cross-references first, with minutes, personal details, and sensitive membership records staying protected.",
            status: "Public order index",
            type: "Orders and lodge heritage",
            icon: "/images/lodges.png",
            contentType: "lodge-heritage",
            cta: "Open Heritage",
            archive: {
                empty: "No public lodge heritage entries have been indexed from Looking East yet.",
                terms: ["lodge", "installation", "installed", "degree", "degrees", "initiation", "passing", "raising", "master", "warden", "officer", "officers", "photo", "photograph", "visit", "visitation"],
                categories: ["Events", "Province", "Reference", "Article"],
            },
            plan: {
                summary: "The lodge heritage index will separate public historical material from protected member records. Public pages can show lodge-level events, installations, photographs, meeting notices, and research notes while private resources retain personal details.",
                sections: [
                    {
                        title: "Candidate indexes",
                        items: ["Installations and officer changes", "Degree ceremonies and lodge milestones", "Photographs and captioned archive images", "Lodge meeting notices, anniversaries, and visiting records"],
                    },
                    {
                        title: "Protection model",
                        items: ["Publish lodge-level summaries and source references", "Keep sensitive member data behind verified login", "Link public entries to deeper Resources records only where permission allows"],
                    },
                ],
            },
        },
        {
            title: "Odes, Hymns & Toasts",
            subtitle: "Public lodge odes, hymns, festive-board toasts, degree music, words, and source notes.",
            detail: "This collection gathers public ceremonial and social texts from archive material, keeping source references visible while avoiding protected or private ritual material.",
            status: "Public cultural index",
            type: "Cultural archive",
            icon: "/images/odes.svg",
            contentType: "public-document-collection",
            collectionKey: "odes-hymns-toasts",
            cta: "Open Odes",
            plan: {
                summary: "Odes, hymns, and toasts will be treated as a cultural archive: public words, tune references, occasion notes, lodge usage, dates, and source documents where publication is appropriate.",
                sections: [
                    {
                        title: "Candidate indexes",
                        items: ["Opening and closing odes", "Festive-board toasts and responses", "Hymns, tune names, and printed programmes", "Occasion-specific songs, anniversary material, and lodge traditions"],
                    },
                    {
                        title: "Protection model",
                        items: ["Publish only public or permission-safe text", "Record source, date, lodge, occasion, and document reference", "Keep private ritual, copyrighted material, and sensitive notes protected"],
                    },
                ],
            },
        },
        {
            title: "About the Research Model",
            subtitle: "How public collections, protected records, archive evidence, and future data pipes fit together.",
            detail: "Connaught's research library is being built as a layered archive: public indexes surface what can be shared, while protected workspaces preserve richer records for verified users.",
            status: "Public explanation",
            type: "Archive model",
            icon: "/images/about2.png",
            contentType: "collection-plan",
            cta: "Open About",
            plan: {
                summary: "The model starts with source material, extracts structured public evidence, and keeps the original document trail visible. Where content is sensitive, the public page shows only summary metadata and leaves detailed records behind login.",
                sections: [
                    {
                        title: "How it works",
                        items: ["Upload or import source material into the archive", "Extract searchable indexes by topic, person, lodge, charity, date, and page", "Publish public summaries with links back to source pages where allowed", "Route protected material to logged-in workspaces for archivists and researchers"],
                    },
                    {
                        title: "What comes next",
                        items: ["Use the Looking East pattern for newsletters, charities, lodges, and future collections", "Pipe curated Resources data into public research summaries", "Add charts, appendices, photographs, and source evidence as each collection matures"],
                    },
                ],
            },
        },
    ];

    const researchGroups = [
        {
            title: "Interactive Timeline",
            eyebrow: "Public history",
            description: "Interesting people, places, events, photographs, and archive moments with visible source notes.",
            icon: "/images/highlights.png",
            cta: "Open Timeline",
            entries: researchEntries.filter((entry) => entry.contentType === "historical-highlights"),
        },
        {
            title: "Orders & Lodge Heritage",
            eyebrow: "Public heritage",
            description: "Irish Craft, Royal Arch, Knight Masons, and Knights Templar public indexes for photos, installations, degrees, events, and historical notes.",
            icon: "/images/lodges.png",
            cta: "Open Heritage",
            entries: researchEntries.filter((entry) => entry.title === "Orders & Lodge Heritage"),
        },
        {
            title: "Newsletters",
            eyebrow: "Public archive",
            description: "Published newsletter collections and searchable issue indexes.",
            icon: "/images/newsletters.svg",
            cta: "Open Newsletters",
            entries: researchEntries.filter((entry) => entry.contentType === "looking-east"),
        },
        {
            title: "Research Papers",
            eyebrow: "Publication workspace",
            description: "Published research papers and PDF production tools for Connaught 361 research publications.",
            icon: "/images/research.png",
            cta: "Open Papers",
            entries: researchEntries.filter((entry) => ["public-research-paper", "research-papers"].includes(entry.contentType)),
        },
        {
            title: "Charity & Fundraising",
            eyebrow: "Community evidence",
            description: "Archive-derived charity work, fundraising activity, appeals, outcomes, and future Resources links.",
            icon: "/images/charity.svg",
            cta: "Open Charity",
            entries: researchEntries.filter((entry) => ["Charity & Fundraising", "Tirmochree Lodge Charity Work"].includes(entry.title)),
        },
        {
            title: "Odes, Hymns & Toasts",
            eyebrow: "Cultural archive",
            description: "Public lodge odes, hymns, festive-board toasts, music references, occasion notes, and source records.",
            icon: "/images/odes.svg",
            cta: "Open Odes",
            entries: researchEntries.filter((entry) => entry.title === "Odes, Hymns & Toasts"),
        },
    ].filter((group) => group.entries.length);

    const openCollectionEntry = (entry) => {
        setSelectedCollection(null);
        if (entry.action) {
            entry.action();
            return;
        }
        setSelectedTopic(entry);
    };

    const openResearchGroup = (group) => {
        if (group.action) {
            group.action();
            return;
        }
        if (group.entries.length === 1) {
            openCollectionEntry(group.entries[0]);
            return;
        }
        setSelectedCollection(group);
    };

    const openNewsletterEntry = (issue, section = null) => {
        const entry = researchEntries.find((candidate) => candidate.contentType === "looking-east");
        setSelectedCollection(null);
        setSelectedTopic(entry);
        setSelectedNewsletterId(issue.id);
        setSelectedNewsletterSection(section ? { issue, section } : null);
        if (section) pendingNewsletterEntryScrollRef.current = true;
    };

    useEffect(() => {
        if (selectedTopic?.contentType !== "survey-results") return;
        setSelectedCollection(null);
        setSelectedTopic(null);
        navigate("/surveys");
    }, [selectedTopic, navigate]);

    const getResearchSessionId = () => {
        const key = "research_session_id";
        const existing = sessionStorage.getItem(key);
        if (existing) return existing;
        const generated = `research-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem(key, generated);
        return generated;
    };

    const getResearchVisitorId = () => {
        const key = "research_visitor_id";
        try {
            const existing = window.localStorage?.getItem(key);
            if (existing) return existing;
            const generated = `visitor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
            window.localStorage?.setItem(key, generated);
            return generated;
        } catch {
            return getResearchSessionId();
        }
    };

    const logResearchAnalytics = async (interactionType, additionalData = {}) => {
        try {
            await axios.post("/api/public/research-analytics/event", {
                interaction_type: interactionType,
                request_path: window.location.pathname,
                session_id: getResearchSessionId(),
                visitor_id: getResearchVisitorId(),
                additional_data: additionalData,
            });
        } catch (err) {
            console.warn("Could not log anonymous research analytics:", err.message);
        }
    };

    const loadLodgeHeritage = async (force = false) => {
        if (!force && (lodgeHeritage || lodgeHeritageLoading)) return;
        setLodgeHeritageError("");
        setLodgeHeritageLoading(true);
        try {
            const { data } = await axios.get("/api/public/lodge-heritage", { withCredentials: true });
            setLodgeHeritage(data);
        } catch (err) {
            console.error("Could not load public lodge heritage:", err);
            setLodgeHeritageError(err.response?.data?.error || "Could not load the public lodge heritage index.");
        } finally {
            setLodgeHeritageLoading(false);
        }
    };

    const loadCharityFundraising = async (force = false) => {
        if (!force && (charityFundraising || charityFundraisingLoading)) return;
        setCharityFundraisingError("");
        setCharityFundraisingLoading(true);
        try {
            const { data } = await axios.get("/api/public/charity-fundraising", { withCredentials: true });
            setCharityFundraising(data);
        } catch (err) {
            console.error("Could not load charity and fundraising research:", err);
            setCharityFundraisingError(err.response?.data?.error || "Could not load the charity and fundraising index.");
        } finally {
            setCharityFundraisingLoading(false);
        }
    };

    const loadPublicDocumentCollection = async (entry, force = false) => {
        const collectionKey = entry?.collectionKey;
        if (!collectionKey) return;
        const current = publicCollections[collectionKey];
        if (!force && (current?.data || current?.loading)) return;
        setPublicCollectionError("");
        setPublicCollections((state) => ({
            ...state,
            [collectionKey]: { ...(state[collectionKey] || {}), loading: true, error: "" },
        }));
        try {
            const { data } = await axios.get(`/api/public/research-collections/${encodeURIComponent(collectionKey)}`, { withCredentials: true });
            setPublicCollections((state) => ({
                ...state,
                [collectionKey]: { data, loading: false, error: "" },
            }));
        } catch (err) {
            console.error("Could not load public document collection:", err);
            const message = err.response?.data?.error || "Could not load this public collection.";
            setPublicCollectionError(message);
            setPublicCollections((state) => ({
                ...state,
                [collectionKey]: { ...(state[collectionKey] || {}), loading: false, error: message },
            }));
        }
    };

    const loadHistoricalHighlights = async (force = false) => {
        if (!force && (historicalHighlights.length || historicalHighlightsLoading)) return;
        setHistoricalHighlightsError("");
        setHistoricalHighlightsLoading(true);
        try {
            const { data } = await axios.get("/api/yearly-highlights", {
                params: { publicOnly: canEditPublicCollections ? "false" : "true" },
                withCredentials: true,
            });
            setHistoricalHighlights(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Could not load historical highlights:", err);
            setHistoricalHighlightsError(err.response?.data?.error || "Could not load the interactive timeline.");
        } finally {
            setHistoricalHighlightsLoading(false);
        }
    };

    const selectedNewsletterIssue = useMemo(() => {
        if (!newsletterIssues.length) return null;
        return newsletterIssues.find((issue) => issue.id === selectedNewsletterId) || newsletterIssues[0];
    }, [newsletterIssues, selectedNewsletterId]);

    const visibleNewsletterIssues = useMemo(() => {
        return newsletterIssues
            .filter((issue) => dateInResearchRange(issue.dateCreated || issue.date, newsletterDateFrom, newsletterDateTo))
            .sort((a, b) => compareResearchDateValues(a.dateCreated || a.date, b.dateCreated || b.date, newsletterSort));
    }, [newsletterIssues, newsletterDateFrom, newsletterDateTo, newsletterSort]);

    const formatPublicIssueLabel = (issue) => {
        const sources = [issue?.title, issue?.description, issue?.filename, issue?.documentPath, issue?.issue].filter(Boolean);
        for (const source of sources) {
            const explicit = String(source).match(/issue\s*0?(\d{1,2})\s*[-_/ ]\s*(20\d{2})/i);
            if (explicit) return `Issue ${String(Number(explicit[1])).padStart(2, "0")}-${explicit[2]}`;
        }
        const date = issue?.dateCreated ? new Date(issue.dateCreated) : null;
        if (!date || Number.isNaN(date.getTime())) return issue?.issue || "Issue";
        const issueNumber = Math.max(1, Math.min(4, Math.ceil((date.getMonth() + 1) / 3)));
        return `Issue ${String(issueNumber).padStart(2, "0")}-${date.getFullYear()}`;
    };

    const newsletterStats = useMemo(() => {
        const totalPages = newsletterIssues.reduce((sum, issue) => sum + Number(issue.pages || 0), 0);
        const years = newsletterIssues
            .map((issue) => issue.dateCreated ? new Date(issue.dateCreated).getFullYear() : null)
            .filter(Boolean);
        const yearRange = years.length ? `${Math.min(...years)}-${Math.max(...years)}` : "Archive";
        return { totalPages, yearRange };
    }, [newsletterIssues]);

    const newsletterMatches = useMemo(() => {
        const query = newsletterSearch.trim().toLowerCase();
        const category = newsletterCategory.trim().toLowerCase();
        const hasDateFilter = Boolean(newsletterDateFrom || newsletterDateTo);
        const issueScope = query || category || hasDateFilter ? visibleNewsletterIssues : (selectedNewsletterIssue ? [selectedNewsletterIssue] : []);
        const matches = [];

        issueScope.forEach((issue) => {
            (issue.sections || []).forEach((section) => {
                if (category && String(section.category || "").trim().toLowerCase() !== category) return;
                const haystack = normalizeSearchText([
                    issue.title,
                    issue.issue,
                    issue.date,
                    issue.description,
                    section.title,
                    section.category,
                    section.snippet,
                    ...(section.tags || []),
                    String(section.page),
                ]);
                if (!query || haystack.includes(query)) {
                    matches.push({ issue, section });
                }
            });
        });

        return matches.sort((a, b) => {
            const queryOrCategoryActive = Boolean(query || category || hasDateFilter);
            if (queryOrCategoryActive) {
                const dateCompare = compareResearchDateValues(a.issue?.dateCreated || a.issue?.date, b.issue?.dateCreated || b.issue?.date, newsletterSort);
                if (dateCompare) return dateCompare;
            }

            const pageA = Number(a.section?.page || a.section?.printedPage || 9999);
            const pageB = Number(b.section?.page || b.section?.printedPage || 9999);
            const printedA = Number(a.section?.printedPage || a.section?.page || 9999);
            const printedB = Number(b.section?.printedPage || b.section?.page || 9999);
            const sortA = Number(a.section?.sortOrder || 0);
            const sortB = Number(b.section?.sortOrder || 0);

            return (
                pageA - pageB ||
                printedA - printedB ||
                sortA - sortB ||
                String(a.section?.title || "").localeCompare(String(b.section?.title || ""))
            );
        });
    }, [newsletterSearch, newsletterCategory, selectedNewsletterIssue, visibleNewsletterIssues, newsletterDateFrom, newsletterDateTo, newsletterSort]);

    useEffect(() => {
        if (!["looking-east", "archive-topic"].includes(selectedTopic?.contentType) || newsletterLoaded || newsletterLoading) return;

        fetchLookingEastIndex();
    }, [newsletterLoaded, newsletterLoading, selectedTopic]);

    useEffect(() => {
        if (selectedTopic?.contentType !== "looking-east") return;
        setNewsletterDateFrom("");
        setNewsletterDateTo("");
        setNewsletterSort("newest");
    }, [selectedTopic]);

    useEffect(() => {
        if (!pendingNewsletterEntryScrollRef.current || selectedTopic?.contentType !== "looking-east" || !selectedNewsletterSection) return;
        const timer = window.setTimeout(() => {
            selectedNewsletterDetailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            selectedNewsletterDetailRef.current?.focus({ preventScroll: true });
            pendingNewsletterEntryScrollRef.current = false;
        }, 80);
        return () => window.clearTimeout(timer);
    }, [selectedTopic?.contentType, selectedNewsletterSection]);

    useEffect(() => {
        if (selectedTopic?.contentType !== "lodge-heritage") return;
        setLodgeHeritageSearch("");
        setLodgeHeritageOrder("all");
        setLodgeHeritageCategory("all");
        setSelectedLodgeHeritageId("");
        setLodgeHeritageDateFrom("");
        setLodgeHeritageDateTo("");
        setLodgeHeritageSort("newest");
        loadLodgeHeritage(true);
    }, [selectedTopic, user?.role, user?.id]);

    useEffect(() => {
        if (selectedTopic?.contentType !== "charity-fundraising") return;
        setCharityFundraisingSearch("");
        setCharityFundraisingLodge("");
        setCharityFundraisingCategory("all");
        setCharityFundraisingDateFrom("");
        setCharityFundraisingDateTo("");
        setCharityFundraisingSort("newest");
        loadCharityFundraising(true);
    }, [selectedTopic, user?.role, user?.id]);

    useEffect(() => {
        if (selectedTopic?.contentType !== "public-document-collection") return;
        setPublicCollectionSearch("");
        setPublicCollectionCategory("");
        setPublicCollectionDateFrom("");
        setPublicCollectionDateTo("");
        setPublicCollectionSort("newest");
        loadPublicDocumentCollection(selectedTopic);
    }, [selectedTopic]);

    useEffect(() => {
        if (selectedTopic?.contentType !== "historical-highlights") return;
        loadHistoricalHighlights();
    }, [selectedTopic, canEditPublicCollections]);

    useEffect(() => {
        const logAndLoad = async () => {
            await logResearchAnalytics("research_page_view", { page: "ResearchPage" });
        };
        logAndLoad();
    }, []);

    const getPublicationQueueCollectionKey = (topic = selectedTopic) => {
        if (!topic) return "";
        if (topic.contentType === "looking-east") return "looking-east";
        if (topic.contentType === "lodge-heritage") return "lodge-heritage";
        return topic.collectionKey || "";
    };

    const loadPublicationQueue = async (openAfterLoad = false, collectionKey = getPublicationQueueCollectionKey()) => {
        if (!canEditPublicCollections) return;
        setPublicationQueueLoading(true);
        setPublicationQueueError("");
        try {
            const params = collectionKey ? { collectionKey } : {};
            const res = await axios.get("/api/admin/research-publication-queue", { params, withCredentials: true });
            setPublicationQueue(res.data || { items: [], groups: [], count: 0 });
            if (openAfterLoad) setPublicationQueueOpen(true);
        } catch (err) {
            console.error("Error loading publication queue:", err);
            setPublicationQueueError(err.response?.data?.error || "Could not load the publication review queue.");
            if (openAfterLoad) setPublicationQueueOpen(true);
        } finally {
            setPublicationQueueLoading(false);
        }
    };

    useEffect(() => {
        if (!canEditPublicCollections) {
            setPublicationQueue(null);
            setPublicationQueueOpen(false);
            return;
        }
        loadPublicationQueue(false, getPublicationQueueCollectionKey());
    }, [canEditPublicCollections, user?.id, user?.role, selectedTopic?.contentType, selectedTopic?.collectionKey]);

    const fetchLookingEastIndex = async (force = false) => {
        setNewsletterLoading(!force);
        setNewsletterRefreshing(force);
        setNewsletterError("");
        try {
            const res = await axios.get(`/api/public/looking-east${force ? "?refresh=true" : ""}`, { withCredentials: true });
            if (!Array.isArray(res.data?.issues)) {
                throw new Error("Newsletter index response did not include an issues array.");
            }

            const issues = res.data.issues;
            setNewsletterIndexMeta({
                canManageNewsletter: Boolean(res.data.canManageNewsletter),
                totalIssues: Number(res.data.totalIssues || issues.length),
                visibleIssues: Number(res.data.visibleIssues || issues.length),
                hiddenDraftIssues: Number(res.data.hiddenDraftIssues || 0),
                sourceDocuments: Number(res.data.sourceDocuments || 0),
            });

            if (canEditNewsletter && !res.data.canManageNewsletter) {
                setNewsletterError("You are shown as an archivist in the browser, but the server did not receive your archivist session. Please log out and back in, then refresh the index.");
            } else if (!issues.length && Number(res.data.totalIssues || 0) > 0) {
                setNewsletterError(canEditNewsletter
                    ? "The archive has Looking East issues, but none were returned to this view. Please refresh the index or sign in again as archivist."
                    : "No published Looking East issues are available yet.");
            } else if (!issues.length && Number(res.data.sourceDocuments || 0) === 0) {
                setNewsletterError("No Looking East PDFs were found in the document archive. Check that the uploaded files include Looking East in the title, filename, description, or path.");
            }

            setNewsletterIssues(issues);
            setSelectedNewsletterId((current) => {
                if (current && issues.some((issue) => issue.id === current)) return current;
                return issues[0]?.id || "";
            });
            setSelectedNewsletterSection((current) => {
                if (current?.issue?.id) {
                    const refreshedIssue = issues.find((issue) => issue.id === current.issue.id);
                    const refreshedSection = refreshedIssue?.sections?.find((section) => section.sourceKey === current.section?.sourceKey);
                    if (refreshedIssue && refreshedSection) return { issue: refreshedIssue, section: refreshedSection };
                }
                return null;
            });
            setNewsletterLoaded(true);
        } catch (err) {
            console.error("Error loading Looking East index:", err);
            setNewsletterError("Could not load the Looking East newsletter index.");
        } finally {
            setNewsletterLoading(false);
            setNewsletterRefreshing(false);
        }
    };

    const handleCardKeyDown = (event, callback) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            callback();
        }
    };

    const getIssuePdfUrl = (issue = selectedNewsletterIssue, page, options = {}) => {
        if (!issue?.documentPath) return "";
        const offsetExtractedPage = options.offsetExtractedPage !== false;
        const pdfPage = page ? Math.max(1, Number(page) + (offsetExtractedPage ? 1 : 0)) : "";
        const pageHash = pdfPage ? `#page=${pdfPage}` : "";
        return `${window.location.origin}/documents/${encodeDocumentPath(issue.documentPath)}${pageHash}`;
    };

    const logIssuePdfOpen = (issue = selectedNewsletterIssue, page) => {
        logResearchAnalytics("research_document_open", {
            collection: "Looking East",
            issue: issue?.issue || issue?.title || "",
            page: page || "",
            documentId: issue?.id || "",
        });
    };

    const openIssuePdf = (issue = selectedNewsletterIssue, page, options = {}) => {
        const url = getIssuePdfUrl(issue, page, options);
        if (!url) return;
        window.open(url, "_blank", "noopener,noreferrer");
        logIssuePdfOpen(issue, page);
    };

    const getIssueCoverUrl = (issue = selectedNewsletterIssue) => {
        if (!issue?.id) return "";
        return `${window.location.origin}/api/public/looking-east/${encodeURIComponent(issue.id)}/cover`;
    };

    const updateIssuePublication = async (issue, published) => {
        if (!issue?.id) return;
        setNewsletterSaving(true);
        setNewsletterEditorError("");
        try {
            await axios.post("/api/admin/looking-east/issues/publish", {
                documentId: issue.id,
                published,
            });
            setNewsletterIssues((current) => current.map((candidate) => (
                candidate.id === issue.id ? { ...candidate, published } : candidate
            )));
            if (canEditPublicCollections) await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error updating newsletter issue publication:", err);
            setNewsletterEditorError(err.response?.data?.error || "Could not update newsletter publication status.");
        } finally {
            setNewsletterSaving(false);
        }
    };

    const updatePublicCollectionPublication = async (entry, document, published) => {
        if (!entry?.collectionKey || !document?.id) return;
        setPublicCollectionSaving(true);
        setPublicCollectionError("");
        try {
            await axios.post(`/api/admin/research-collections/${encodeURIComponent(entry.collectionKey)}/documents/publish`, {
                documentId: document.id,
                published,
            });
            if (entry.contentType === "charity-fundraising") {
                await loadCharityFundraising(true);
            } else {
                await loadPublicDocumentCollection(entry, true);
            }
            if (canEditPublicCollections) await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error updating public collection publication:", err);
            setPublicCollectionError(err.response?.data?.error || "Could not update this collection entry.");
        } finally {
            setPublicCollectionSaving(false);
        }
    };

    const updateLodgeHeritagePublication = async (document, published) => {
        if (!document?.id) return;
        setPublicCollectionSaving(true);
        setPublicCollectionError("");
        try {
            await axios.post("/api/admin/research-collections/lodge-heritage/documents/publish", {
                documentId: document.id,
                published,
            });
            await loadLodgeHeritage(true);
            if (canEditPublicCollections) await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error updating lodge heritage publication:", err);
            setPublicCollectionError(err.response?.data?.error || "Could not update this lodge heritage entry.");
        } finally {
            setPublicCollectionSaving(false);
        }
    };

    const updateLodgeHeritageBodyPublication = async (lodge, published) => {
        if (!lodge?.id) return;
        setPublicCollectionSaving(true);
        setPublicCollectionError("");
        try {
            await axios.post("/api/admin/research-collections/lodge-heritage/lodges/publish", {
                lodgeId: lodge.id,
                published,
            });
            await loadLodgeHeritage(true);
            if (canEditPublicCollections) await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error updating lodge heritage body publication:", err);
            setPublicCollectionError(err.response?.data?.error || "Could not update this lodge or body.");
        } finally {
            setPublicCollectionSaving(false);
        }
    };

    const updateLodgeHeritageFeaturedImage = async (lodge, document) => {
        if (!lodge?.id || !document?.id) return;
        setPublicCollectionSaving(true);
        setPublicCollectionError("");
        try {
            await axios.post("/api/admin/research-collections/lodge-heritage/lodges/featured-image", {
                lodgeId: lodge.id,
                documentId: document.id,
            });
            await loadLodgeHeritage(true);
        } catch (err) {
            console.error("Error updating lodge heritage archive image:", err);
            setPublicCollectionError(err.response?.data?.error || "Could not update this lodge or body archive image.");
        } finally {
            setPublicCollectionSaving(false);
        }
    };

    const updateHistoricalHighlightPublication = async (highlight, published) => {
        if (!highlight?.id) return;
        setHistoricalHighlightsSaving(true);
        setHistoricalHighlightsError("");
        try {
            await axios.post(`/api/admin/yearly-highlights/${encodeURIComponent(highlight.id)}/publish`, {
                published,
                displayOnHomepage: published ? Boolean(highlight.display_on_homepage) : false,
            }, { withCredentials: true });
            await loadHistoricalHighlights(true);
            if (canEditPublicCollections) await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error updating historical highlight publication:", err);
            setHistoricalHighlightsError(err.response?.data?.error || "Could not update this historical highlight.");
        } finally {
            setHistoricalHighlightsSaving(false);
        }
    };

    const openNewsletterEditor = (mode, issue = selectedNewsletterIssue, section = null, defaults = {}) => {
        if (!issue) return;
        const sortOrder = section ? (issue.sections || []).findIndex((candidate) => candidate.sourceKey === section.sourceKey) : (issue.sections || []).length;
        const defaultCategory = defaults.category || "Article";
        setNewsletterEditorError("");
        setNewsletterEditor({
            mode,
            issue,
            sourceKey: section?.sourceKey || "",
            title: section?.title || "",
            category: section?.category || defaultCategory,
            advertType: section?.metadata?.advertType || defaults.advertType || "General advert",
            advertDate: section?.metadata?.advertDate || defaults.advertDate || "",
            page: section?.page || 1,
            printedPage: section?.printedPage || section?.page || 1,
            snippet: section?.snippet || "",
            tags: (section?.tags || []).join(", "),
            sortOrder: sortOrder >= 0 ? sortOrder : (issue.sections || []).length,
            manual: mode === "add" || Boolean(section?.manual),
        });
    };

    const saveNewsletterEditor = async (event) => {
        event.preventDefault();
        if (!newsletterEditor?.issue?.id) return;
        setNewsletterSaving(true);
        setNewsletterEditorError("");
        try {
            const res = await axios.post("/api/admin/looking-east/sections", {
                documentId: newsletterEditor.issue.id,
                sourceKey: newsletterEditor.sourceKey || undefined,
                title: newsletterEditor.title,
                category: newsletterEditor.category,
                advertType: newsletterEditor.advertType,
                advertDate: newsletterEditor.advertDate,
                page: newsletterEditor.page,
                printedPage: newsletterEditor.printedPage,
                snippet: newsletterEditor.snippet,
                tags: newsletterEditor.tags,
                manual: newsletterEditor.manual,
                sortOrder: newsletterEditor.sortOrder,
            });
            await fetchLookingEastIndex(true);
            const saved = res.data;
            setSelectedNewsletterId(newsletterEditor.issue.id);
            setSelectedNewsletterSection((current) => ({
                issue: newsletterEditor.issue,
                section: {
                    ...(current?.section || {}),
                    ...saved,
                    page: Number(saved.page || newsletterEditor.page || 1),
                    printedPage: Number(saved.printedPage || newsletterEditor.printedPage || newsletterEditor.page || 1),
                },
            }));
            setNewsletterEditor(null);
        } catch (err) {
            console.error("Error saving newsletter entry:", err);
            setNewsletterEditorError(err.response?.data?.error || "Could not save this newsletter entry.");
        } finally {
            setNewsletterSaving(false);
        }
    };

    const hideNewsletterSection = async (issue, section) => {
        if (!issue?.id || !section?.sourceKey) return;
        setNewsletterSaving(true);
        setNewsletterEditorError("");
        try {
            await axios.post("/api/admin/looking-east/sections/hide", {
                documentId: issue.id,
                sourceKey: section.sourceKey,
                title: section.title,
                page: section.page,
                manual: section.manual,
                hidden: true,
            });
            await fetchLookingEastIndex(true);
            setSelectedNewsletterSection(issue.sections?.[0] ? { issue, section: issue.sections[0] } : null);
        } catch (err) {
            console.error("Error hiding newsletter entry:", err);
            setNewsletterEditorError(err.response?.data?.error || "Could not hide this newsletter entry.");
        } finally {
            setNewsletterSaving(false);
        }
    };

    const lookingEastMasthead = (variant = "full") => (
        <div className={`looking-east-masthead ${variant !== "full" ? variant : ""}`} aria-label="Looking East newsletter masthead">
            <div className="looking-east-crest">
                <img src="/images/looking-east-pgl-crest.png" alt="" aria-hidden="true" />
            </div>
            <div className="looking-east-title-panel">
                <img src="/images/looking-east-title.png" alt="" aria-hidden="true" />
                <span>The Newsletter of the Provincial Grand Lodge of South Africa, Northern IC</span>
            </div>
            <div className="looking-east-square">
                <img src="/images/looking-east-square-compasses.png" alt="" aria-hidden="true" />
            </div>
        </div>
    );

    const openCollectionDocument = (entry, document) => {
        if (!document?.url) return;
        logResearchAnalytics("research_document_open", {
            collection: entry?.title || entry?.collectionKey || "",
            documentId: document.id,
            title: document.title || "",
        });
        window.open(document.url, "_blank", "noopener,noreferrer");
    };

    const renderArchiveUpdateNotice = () => (
        <p className="research-public-update-notice">
            Note: public archive records are being reviewed and updated continuously. If you use this material, please cite the Connaught Research Lodge Archive and any listed source shown with the entry.
        </p>
    );

    const getPublicationQueueItemKey = (item) => [
        item.queueType,
        item.collectionKey,
        item.itemId,
        item.documentId,
        item.lodgeId,
        item.highlightId,
    ].filter(Boolean).join(":");

    const openPublicationQueueItem = async (item) => {
        const entry = researchEntries.find((candidate) => (
            candidate.collectionKey === item.collectionKey ||
            (item.queueType === "looking-east" && candidate.contentType === "looking-east") ||
            (item.queueType === "historical-highlight" && candidate.contentType === "historical-highlights") ||
            (item.collectionKey === "lodge-heritage" && candidate.contentType === "lodge-heritage")
        ));

        if (entry) {
            setSelectedCollection(null);
            setSelectedTopic(entry);
        }

        if (item.queueType === "looking-east") {
            await fetchLookingEastIndex(true);
            setSelectedNewsletterId(item.documentId || "");
            return;
        }

        if (item.collectionKey === "charity-fundraising") {
            await loadCharityFundraising(true);
            return;
        }

        if (item.collectionKey === "lodge-heritage") {
            await loadLodgeHeritage(true);
            if (item.lodgeId) setSelectedLodgeHeritageId(item.lodgeId);
            return;
        }

        if (item.queueType === "historical-highlight") {
            await loadHistoricalHighlights(true);
            return;
        }

        if (entry?.contentType === "public-document-collection") {
            await loadPublicDocumentCollection(entry, true);
        }
    };

    const publishPublicationQueueItem = async (item) => {
        const itemKey = getPublicationQueueItemKey(item);
        setPublicationQueueSavingId(itemKey);
        setPublicationQueueError("");
        try {
            if (item.queueType === "looking-east") {
                await axios.post("/api/admin/looking-east/issues/publish", {
                    documentId: item.documentId,
                    published: true,
                });
                setNewsletterIssues((current) => current.map((issue) => (
                    issue.id === item.documentId ? { ...issue, published: true } : issue
                )));
            } else if (item.queueType === "lodge-body") {
                await axios.post("/api/admin/research-collections/lodge-heritage/lodges/publish", {
                    lodgeId: item.lodgeId,
                    published: true,
                });
                if (selectedTopic?.contentType === "lodge-heritage") await loadLodgeHeritage(true);
            } else if (item.queueType === "historical-highlight") {
                await axios.post(`/api/admin/yearly-highlights/${encodeURIComponent(item.highlightId || item.itemId)}/publish`, {
                    published: true,
                    displayOnHomepage: Boolean(item.displayOnHomepage),
                }, { withCredentials: true });
                if (selectedTopic?.contentType === "historical-highlights") await loadHistoricalHighlights(true);
            } else if (item.requiresLodgePublish && item.lodgeId) {
                if (item.documentId && item.collectionKey) {
                    await axios.post(`/api/admin/research-collections/${encodeURIComponent(item.collectionKey)}/documents/publish`, {
                        documentId: item.documentId,
                        published: true,
                    });
                }
                await axios.post("/api/admin/research-collections/lodge-heritage/lodges/publish", {
                    lodgeId: item.lodgeId,
                    published: true,
                });
                if (selectedTopic?.contentType === "lodge-heritage") await loadLodgeHeritage(true);
            } else if (item.documentId && item.collectionKey) {
                await axios.post(`/api/admin/research-collections/${encodeURIComponent(item.collectionKey)}/documents/publish`, {
                    documentId: item.documentId,
                    published: true,
                });
                if (item.collectionKey === "lodge-heritage" && item.lodgeId) {
                    await axios.post("/api/admin/research-collections/lodge-heritage/lodges/publish", {
                        lodgeId: item.lodgeId,
                        published: true,
                    });
                }
                if (item.collectionKey === "charity-fundraising" && selectedTopic?.contentType === "charity-fundraising") {
                    await loadCharityFundraising(true);
                } else if (item.collectionKey === "lodge-heritage" && selectedTopic?.contentType === "lodge-heritage") {
                    await loadLodgeHeritage(true);
                } else {
                    const entry = researchEntries.find((candidate) => candidate.collectionKey === item.collectionKey);
                    if (entry && selectedTopic?.collectionKey === item.collectionKey) {
                        await loadPublicDocumentCollection(entry, true);
                    }
                }
            }
            await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error publishing queue item:", err);
            setPublicationQueueError(err.response?.data?.error || "Could not publish this item.");
        } finally {
            setPublicationQueueSavingId("");
        }
    };

    const dismissPublicationQueueItem = async (item) => {
        const itemKey = getPublicationQueueItemKey(item);
        if (!window.confirm("Remove this item from the Needs Publishing queue? The source record will stay in the archive, but it will not be published from this public review list.")) {
            return;
        }
        setPublicationQueueSavingId(itemKey);
        setPublicationQueueError("");
        try {
            await axios.post("/api/admin/research-publication-queue/dismiss", {
                queueType: item.queueType,
                collectionKey: item.collectionKey,
                documentId: item.documentId,
                lodgeId: item.lodgeId,
            });
            await loadPublicationQueue(false, getPublicationQueueCollectionKey());
        } catch (err) {
            console.error("Error dismissing queue item:", err);
            setPublicationQueueError(err.response?.data?.error || "Could not remove this item from the review queue.");
        } finally {
            setPublicationQueueSavingId("");
        }
    };

    const renderPublicationQueueButton = (variant = "") => {
        if (!canEditPublicCollections) return null;
        const count = Number(publicationQueue?.count || 0);
        const countLabel = publicationQueueError
            ? "!"
            : (!publicationQueue && publicationQueueLoading ? "..." : count.toLocaleString());
        return (
            <button
                type="button"
                className={`research-publication-queue-button ${variant}`.trim()}
                onClick={() => loadPublicationQueue(true, getPublicationQueueCollectionKey())}
                disabled={publicationQueueLoading}
                title={publicationQueueError || "Open draft public records waiting for publication"}
            >
                <span>{publicationQueueLoading ? "Checking..." : "Needs Publishing"}</span>
                <strong>{countLabel}</strong>
            </button>
        );
    };

    const renderPublicationQueueModal = () => {
        if (!publicationQueueOpen || !canEditPublicCollections) return null;
        const items = Array.isArray(publicationQueue?.items) ? publicationQueue.items : [];
        const query = publicationQueueSearch.trim().toLowerCase();
        const filteredItems = items.filter((item) => {
            const haystack = normalizeSearchText([
                item.collectionTitle,
                item.title,
                item.description,
                item.lodge,
                item.documentType,
                item.eventType,
                item.date,
                item.filename,
                item.sourceDocument,
            ]);
            return !query || haystack.includes(query);
        });
        const groupedItems = filteredItems.reduce((groups, item) => {
            const key = item.collectionTitle || item.collectionKey || "Other";
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
            return groups;
        }, {});

        return (
            <div className="research-detail-backdrop" role="dialog" aria-modal="true" aria-labelledby="publication-queue-title">
                <div className="research-detail-modal compact publication-queue-modal">
                    <button
                        type="button"
                        className="research-detail-close"
                        onClick={() => setPublicationQueueOpen(false)}
                        aria-label="Close publication review"
                    >
                        &times;
                    </button>
                    <div className="research-detail-content">
                        <div className="publication-queue-header">
                            <div>
                                <p className="research-eyebrow">Archivist review</p>
                                <h2 id="publication-queue-title">Needs Publishing</h2>
                                <p>Draft public records and published records blocked by draft parent bodies, waiting for review across the public archive sections.</p>
                            </div>
                            <div className="publication-queue-summary">
                                <strong>{Number(publicationQueue?.count || 0)}</strong>
                                <span>review item{Number(publicationQueue?.count || 0) === 1 ? "" : "s"}</span>
                            </div>
                        </div>

                        <div className="publication-queue-toolbar">
                            <input
                                type="search"
                                value={publicationQueueSearch}
                                onChange={(event) => setPublicationQueueSearch(event.target.value)}
                                placeholder="Search title, lodge, type, description..."
                            />
                            <button
                                type="button"
                                className="research-refresh-button"
                                onClick={() => loadPublicationQueue(false, getPublicationQueueCollectionKey())}
                                disabled={publicationQueueLoading}
                            >
                                {publicationQueueLoading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>

                        {publicationQueueError && <p className="research-error">{publicationQueueError}</p>}

                        {publicationQueueLoading && !publicationQueue ? (
                            <p className="research-empty-state">Loading publication queue...</p>
                        ) : filteredItems.length === 0 ? (
                            <p className="research-empty-state">
                                {query ? "No review items match that search." : "No public records are waiting for publication."}
                            </p>
                        ) : (
                            <div className="publication-queue-groups">
                                {Object.entries(groupedItems).map(([groupTitle, groupItems]) => (
                                    <section className="publication-queue-group" key={groupTitle}>
                                        <div className="publication-queue-group-header">
                                            <h3>{groupTitle}</h3>
                                            <span>{groupItems.length} item{groupItems.length === 1 ? "" : "s"}</span>
                                        </div>
                                        <div className="publication-queue-list">
                                            {groupItems.map((item) => {
                                                const itemKey = getPublicationQueueItemKey(item);
                                                const saving = publicationQueueSavingId === itemKey;
                                                return (
                                                    <article className="publication-queue-item" key={itemKey}>
                                                        <div>
                                                            <div className="research-meta-row">
                                                                <span>{item.requiresLodgePublish ? "Body draft" : "Draft"}</span>
                                                                {item.requiresLodgePublish && <span>Document published</span>}
                                                                {item.documentType && <span>{item.documentType}</span>}
                                                                {item.eventType && <span>{item.eventType}</span>}
                                                            </div>
                                                            <h4>{item.title || "Untitled draft item"}</h4>
                                                            <p>
                                                                {item.requiresLodgePublish
                                                                    ? `${item.description || item.filename || "No description recorded yet."} This record is already published, but its lodge/body must also be published before it appears publicly.`
                                                                    : (item.description || item.filename || "No description recorded yet.")}
                                                            </p>
                                                            <div className="publication-queue-item-meta">
                                                                {item.lodge && <span>{item.lodge}</span>}
                                                                {item.date && <span>{formatResearchDate(item.date)}</span>}
                                                                {item.queueType && <span>{item.queueType.replaceAll("-", " ")}</span>}
                                                                {item.sourceDocument && <span>{item.sourceDocument}</span>}
                                                            </div>
                                                        </div>
                                                        <div className="publication-queue-actions">
                                                            <button
                                                                type="button"
                                                                className="research-refresh-button secondary"
                                                                onClick={() => {
                                                                    if (item.documentId) {
                                                                        window.open(documentDownloadUrl(item.documentId, item.url || ""), "_blank", "noopener,noreferrer");
                                                                    } else {
                                                                        openPublicationQueueItem(item);
                                                                    }
                                                                }}
                                                            >
                                                                Review
                                                            </button>
                                                            {item.url && (
                                                                <button
                                                                    type="button"
                                                                    className="research-refresh-button secondary"
                                                                    onClick={() => window.open(documentDownloadUrl(item.documentId, item.url), "_blank", "noopener,noreferrer")}
                                                                >
                                                                    Open Source
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="research-refresh-button publish"
                                                                onClick={() => publishPublicationQueueItem(item)}
                                                                disabled={saving}
                                                            >
                                                                {saving ? "Publishing..." : (item.requiresLodgePublish ? "Publish Lodge/Body" : "Publish")}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="research-refresh-button dismiss"
                                                                onClick={() => dismissPublicationQueueItem(item)}
                                                                disabled={saving}
                                                                hidden={item.queueType === "historical-highlight"}
                                                            >
                                                                {saving ? "Working..." : "Dismiss"}
                                                            </button>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    </section>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderHistoricalHighlights = () => {
        const canManageHighlights = canEditPublicCollections;
        const allHighlights = historicalHighlights
            .slice()
            .sort((a, b) => compareResearchDateValues(a.date || `${a.year || ""}-01-01`, b.date || `${b.year || ""}-01-01`, "oldest"));
        const publicCount = allHighlights.filter((item) => item.is_public !== false).length;
        const draftCount = allHighlights.filter((item) => item.is_public === false).length;
        const timelineImportanceOptions = Array.from(new Set(allHighlights.map((highlight) => highlight.importance).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        const timelineCategoryOptions = Array.from(new Set(allHighlights.map((highlight) => highlight.category).filter(Boolean))).sort((a, b) => a.localeCompare(b));
        const highlights = allHighlights.filter((highlight) => {
            const importanceMatches = interactiveTimelineImportanceFilter === "all" || highlight.importance === interactiveTimelineImportanceFilter;
            const categoryMatches = interactiveTimelineCategoryFilter === "all" || highlight.category === interactiveTimelineCategoryFilter;
            return importanceMatches && categoryMatches;
        });
        const timelineFiltersActive = interactiveTimelineImportanceFilter !== "all" || interactiveTimelineCategoryFilter !== "all";
        const timelineYearCounts = highlights.reduce((counts, highlight) => {
            const yearKey = String(highlight.year || (highlight.date ? new Date(highlight.date).getFullYear() : "") || "Undated");
            counts.set(yearKey, (counts.get(yearKey) || 0) + 1);
            return counts;
        }, new Map());
        const timelineBuckets = highlights.reduce((buckets, highlight) => {
            const yearLabel = String(highlight.year || (highlight.date ? new Date(highlight.date).getFullYear() : "") || "Undated");
            const bucket = buckets.find((item) => item.yearLabel === yearLabel);
            if (bucket) {
                bucket.entries.push(highlight);
            } else {
                buckets.push({ yearLabel, entries: [highlight] });
            }
            return buckets;
        }, []);
        const maxTimelineBucketEntries = Math.max(1, ...timelineBuckets.map((bucket) => bucket.entries.length));
        const selectedHighlight = highlights.find((highlight) => highlight.id === selectedInteractiveTimelineId) || highlights[0] || null;
        const selectedIndex = selectedHighlight ? highlights.findIndex((highlight) => highlight.id === selectedHighlight.id) : -1;
        const selectedSummary = selectedHighlight
            ? selectedHighlight.short_summary || selectedHighlight.description || selectedHighlight.full_detail || "No summary has been captured yet."
            : "";
        const selectedDetail = selectedHighlight ? selectedHighlight.full_detail || selectedHighlight.description || "" : "";
        const selectedHasDetail = Boolean(selectedDetail && selectedDetail !== selectedSummary);
        const selectedExpanded = Boolean(selectedHighlight && expandedHistoricalHighlightId === selectedHighlight.id);
        const selectedSource = selectedHighlight?.source_document || "";
        const selectedPictureCitation = selectedHighlight?.picture_citation || "";
        const selectedKeyFacts = String(selectedHighlight?.key_masonic_facts || "")
            .split(/\r?\n/)
            .map((fact) => fact.trim())
            .filter(Boolean);
        const selectedHasResearchTrail = Boolean(
            selectedSource ||
            selectedPictureCitation ||
            selectedKeyFacts.length ||
            selectedHighlight?.lodge ||
            selectedHighlight?.people ||
            selectedHighlight?.location ||
            (Array.isArray(selectedHighlight?.tags) && selectedHighlight.tags.length > 0)
        );
        const selectedDate = selectedHighlight
            ? formatTimelineDateTag(selectedHighlight.date, selectedHighlight.year, selectedHighlight.date_display_mode)
            : "";
        const selectTimelineHighlight = (highlight) => {
            setSelectedInteractiveTimelineId(highlight.id);
            setExpandedHistoricalHighlightId("");
        };
        const stepTimelineHighlight = (direction) => {
            if (!highlights.length) return;
            const nextIndex = selectedIndex < 0
                ? 0
                : Math.max(0, Math.min(highlights.length - 1, selectedIndex + direction));
            selectTimelineHighlight(highlights[nextIndex]);
        };

        return (
            <div className="research-newsletter-workspace public-document-workspace historical-highlights-workspace">
                <div className="research-public-collection-hero historical-highlights-hero">
                    <div className="research-public-collection-icon" aria-hidden="true">
                        <img src="/images/highlights.png" alt="" />
                    </div>
                    <div className="research-public-collection-copy">
                        <p className="research-eyebrow">Source-backed public history</p>
                        <h3>Interactive Timeline</h3>
                        <p>Interesting archive moments with a visible research trail: source notes, dates, lodges, people, places, and images where available.</p>
                        <div className="research-public-collection-tags">
                            <span>Timeline entries</span>
                            <span>{publicCount} published</span>
                            {canManageHighlights && <span>{draftCount} draft</span>}
                        </div>
                        {canManageHighlights && (
                            <div className="research-newsletter-admin-actions">
                                <button
                                    type="button"
                                    className="research-refresh-button"
                                    onClick={() => loadHistoricalHighlights(true)}
                                    disabled={historicalHighlightsLoading}
                                >
                                    {historicalHighlightsLoading ? "Refreshing..." : "Refresh Highlights"}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="research-public-collection-metrics">
                        <div>
                            <strong>{historicalHighlightsLoading ? "..." : publicCount}</strong>
                            <span>public</span>
                        </div>
                        {canManageHighlights && (
                            <div>
                                <strong>{historicalHighlightsLoading ? "..." : draftCount}</strong>
                                <span>draft</span>
                            </div>
                        )}
                    </div>
                </div>

                {historicalHighlightsError && <div className="research-empty error">{historicalHighlightsError}</div>}
                {historicalHighlightsLoading && <div className="research-empty">Loading interactive timeline...</div>}
                {!historicalHighlightsLoading && !historicalHighlightsError && allHighlights.length === 0 && (
                    <div className="research-empty">No timeline entries have been published yet.</div>
                )}
                {!historicalHighlightsLoading && !historicalHighlightsError && allHighlights.length > 0 && (
                    <div className="interactive-timeline-filters" aria-label="Timeline filters">
                        <label>
                            <span>Importance</span>
                            <select
                                value={interactiveTimelineImportanceFilter}
                                onChange={(event) => setInteractiveTimelineImportanceFilter(event.target.value)}
                            >
                                <option value="all">All importance levels</option>
                                {timelineImportanceOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Category</span>
                            <select
                                value={interactiveTimelineCategoryFilter}
                                onChange={(event) => setInteractiveTimelineCategoryFilter(event.target.value)}
                            >
                                <option value="all">All categories</option>
                                {timelineCategoryOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </label>
                        {timelineFiltersActive && (
                            <button
                                type="button"
                                onClick={() => {
                                    setInteractiveTimelineImportanceFilter("all");
                                    setInteractiveTimelineCategoryFilter("all");
                                }}
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                )}
                {!historicalHighlightsLoading && !historicalHighlightsError && allHighlights.length > 0 && highlights.length === 0 && (
                    <div className="research-empty">No timeline entries match those filters.</div>
                )}
                {!historicalHighlightsLoading && !historicalHighlightsError && selectedHighlight && (
                    <section className="interactive-timeline-experience" aria-label="Interactive historical timeline">
                        <div
                            className={`interactive-timeline-stage ${selectedHighlight.media_path ? "has-image" : "no-image"} ${selectedHighlight.is_public === false ? "draft" : ""}`}
                            style={selectedHighlight.media_path ? { backgroundImage: `url(${selectedHighlight.media_path})` } : undefined}
                        >
                            <div className="interactive-timeline-stage-shade" />
                            <div className="interactive-timeline-brand" aria-hidden="true">
                                <img src="/images/highlights.png" alt="" />
                                <span>Connaught Research Lodge Archive</span>
                            </div>
                            {selectedKeyFacts.length > 0 && (
                                <aside className="interactive-timeline-key-facts" aria-label="Key Masonic facts">
                                    <h4>Key Masonic Facts</h4>
                                    <ul>
                                        {selectedKeyFacts.slice(0, 5).map((fact) => <li key={fact}>{fact}</li>)}
                                    </ul>
                                </aside>
                            )}
                            <div className="interactive-timeline-stage-content">
                                <div className="interactive-timeline-kicker">
                                    <span>{selectedDate}</span>
                                    {selectedHighlight.importance && <span>{selectedHighlight.importance}</span>}
                                    {selectedHighlight.category && <span>{selectedHighlight.category}</span>}
                                    {selectedHighlight.is_public === false && <span>Draft</span>}
                                </div>
                                <h3>{selectedHighlight.title || "Untitled timeline entry"}</h3>
                                {selectedHighlight.media_path && (
                                    <div
                                        className="interactive-timeline-mobile-media"
                                        style={{ backgroundImage: `url(${selectedHighlight.media_path})` }}
                                        aria-hidden="true"
                                    />
                                )}
                                <p>{selectedSummary}</p>
                                {selectedExpanded && (
                                    <div className="interactive-timeline-expanded">
                                        {selectedHasDetail && <p>{selectedDetail}</p>}
                                        {selectedHasResearchTrail && (
                                            <dl>
                                                {selectedSource && (
                                                    <>
                                                        <dt>Source</dt>
                                                        <dd>{selectedSource}</dd>
                                                    </>
                                                )}
                                                {selectedPictureCitation && (
                                                    <>
                                                        <dt>Picture</dt>
                                                        <dd>{selectedPictureCitation}</dd>
                                                    </>
                                                )}
                                                {selectedKeyFacts.length > 0 && (
                                                    <>
                                                        <dt>Key facts</dt>
                                                        <dd>
                                                            <ul className="interactive-timeline-facts">
                                                                {selectedKeyFacts.map((fact) => <li key={fact}>{fact}</li>)}
                                                            </ul>
                                                        </dd>
                                                    </>
                                                )}
                                                {selectedHighlight.lodge && (
                                                    <>
                                                        <dt>Lodge</dt>
                                                        <dd>{selectedHighlight.lodge}</dd>
                                                    </>
                                                )}
                                                {selectedHighlight.people && (
                                                    <>
                                                        <dt>People</dt>
                                                        <dd>{selectedHighlight.people}</dd>
                                                    </>
                                                )}
                                                {selectedHighlight.location && (
                                                    <>
                                                        <dt>Place</dt>
                                                        <dd>{selectedHighlight.location}</dd>
                                                    </>
                                                )}
                                                {Array.isArray(selectedHighlight.tags) && selectedHighlight.tags.length > 0 && (
                                                    <>
                                                        <dt>Tags</dt>
                                                        <dd>{selectedHighlight.tags.slice(0, 8).join(", ")}</dd>
                                                    </>
                                                )}
                                            </dl>
                                        )}
                                    </div>
                                )}
                                <div className="interactive-timeline-actions">
                                    {(selectedHasDetail || selectedHasResearchTrail) && (
                                        <button
                                            type="button"
                                            onClick={() => setExpandedHistoricalHighlightId(selectedExpanded ? "" : selectedHighlight.id)}
                                        >
                                            {selectedExpanded ? "Hide Details" : "View Details"}
                                        </button>
                                    )}
                                    {canManageHighlights && (
                                        <button
                                            type="button"
                                            onClick={() => updateHistoricalHighlightPublication(selectedHighlight, selectedHighlight.is_public === false)}
                                            disabled={historicalHighlightsSaving}
                                        >
                                            {selectedHighlight.is_public === false ? "Publish Entry" : "Move to Draft"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="interactive-timeline-nav">
                                <button
                                    type="button"
                                    onClick={() => stepTimelineHighlight(-1)}
                                    disabled={selectedIndex <= 0}
                                    aria-label="Previous timeline entry"
                                >
                                    Previous
                                </button>
                                <span>{selectedIndex + 1} of {highlights.length}</span>
                                <button
                                    type="button"
                                    onClick={() => stepTimelineHighlight(1)}
                                    disabled={selectedIndex >= highlights.length - 1}
                                    aria-label="Next timeline entry"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                        <div
                            className="interactive-timeline-rail"
                            style={{ "--timeline-row-count": Math.min(5, maxTimelineBucketEntries) }}
                        >
                            <div className="interactive-timeline-rail-head">
                                <span>{timelineBuckets.length} periods</span>
                                <span>{highlights.length} entries</span>
                            </div>
                            <div className="interactive-timeline-track" role="list" aria-label="Timeline entries">
                                {timelineBuckets.map((bucket) => {
                                    const bucketActive = bucket.entries.some((highlight) => highlight.id === selectedHighlight.id);
                                    return (
                                        <div
                                            role="listitem"
                                            className={`interactive-timeline-bucket ${bucketActive ? "active" : ""}`}
                                            key={bucket.yearLabel}
                                        >
                                            <span className="interactive-timeline-dot" />
                                            <strong>{bucket.yearLabel}</strong>
                                            <div className="interactive-timeline-bucket-events">
                                                {bucket.entries.map((highlight) => {
                                                    const isSelected = highlight.id === selectedHighlight.id;
                                                    const dateLabel = formatTimelineDateTag(highlight.date, bucket.yearLabel, highlight.date_display_mode);
                                                    return (
                                                        <button
                                                            type="button"
                                                            className={isSelected ? "active" : ""}
                                                            key={highlight.id}
                                                            onClick={() => selectTimelineHighlight(highlight)}
                                                        >
                                                            {timelineYearCounts.get(bucket.yearLabel) > 1 && <span>{dateLabel}</span>}
                                                            <em>{highlight.title || "Untitled"}</em>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}
            </div>
        );
    };

    const renderPublicDocumentCollection = (entry) => {
        const collection = publicCollections[entry.collectionKey] || {};
        const data = collection.data;
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        const canManageCollection = Boolean(data?.canManage || canEditPublicCollections);
        const categories = [...new Set(entries.map((item) => item.category).filter(Boolean))].sort();
        const categoryCounts = categories.map((name) => ({
            name,
            count: entries.filter((item) => item.category === name).length,
        }));
        const query = publicCollectionSearch.trim().toLowerCase();
        const category = publicCollectionCategory.trim();
        const hasDateFilter = Boolean(publicCollectionDateFrom || publicCollectionDateTo);
        const filteredEntries = entries.filter((item) => {
            if (category && item.category !== category) return false;
            if (!dateInResearchRange(item.date, publicCollectionDateFrom, publicCollectionDateTo)) return false;
            const haystack = normalizeSearchText([
                item.title,
                item.snippet,
                item.description,
                item.lodge,
                item.orderName,
                item.category,
                item.documentType,
                item.eventType,
                item.filename,
                item.date,
            ]);
            return !query || haystack.includes(query);
        }).sort((a, b) => compareResearchDateValues(a.date, b.date, publicCollectionSort));
        const collectionTitle = data?.title || entry.title;
        const collectionDescription = data?.description || entry.detail;
        const visibleCount = Number(data?.visibleEntries || 0);
        const totalCount = Number(data?.totalEntries || 0);
        const draftCount = Number(data?.hiddenDraftEntries || 0);
        const hasActiveFilters = Boolean(query || category || hasDateFilter);
        const collectionContext = entry.collectionKey === "odes-hymns-toasts"
            ? "Ceremonial words, festive-board material, and degree music"
            : "Curated public source records";

        return (
            <div className="research-newsletter-workspace public-document-workspace">
                <div className="research-public-collection-hero">
                    <div className="research-public-collection-icon" aria-hidden="true">
                        {entry.icon ? <img src={entry.icon} alt="" /> : <span>{String(collectionTitle || "A").slice(0, 1)}</span>}
                    </div>
                    <div className="research-public-collection-copy">
                        <p className="research-eyebrow">Public collection index</p>
                        <h3>{collectionTitle}</h3>
                        <p>{collectionDescription}</p>
                        <div className="research-public-collection-tags">
                            <span>{collectionContext}</span>
                            <span>{visibleCount} public entr{visibleCount === 1 ? "y" : "ies"}</span>
                            {totalCount !== visibleCount && <span>{totalCount} matched source records</span>}
                        </div>
                        {canManageCollection && (
                            <div className="research-newsletter-admin-actions">
                                <button
                                    type="button"
                                    className="research-refresh-button"
                                    onClick={() => loadPublicDocumentCollection(entry, true)}
                                    disabled={collection.loading}
                                >
                                    {collection.loading ? "Refreshing..." : "Refresh Index"}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="research-public-collection-metrics">
                        <div>
                            <strong>{collection.loading ? "..." : visibleCount}</strong>
                            <span>public</span>
                        </div>
                        <div>
                            <strong>{collection.loading ? "..." : totalCount}</strong>
                            <span>matched</span>
                        </div>
                        {canManageCollection && (
                            <div>
                                <strong>{collection.loading ? "..." : draftCount}</strong>
                                <span>draft</span>
                            </div>
                        )}
                    </div>
                </div>

                {categoryCounts.length > 0 && (
                    <div className="research-public-category-strip" aria-label="Collection categories">
                        <button
                            type="button"
                            className={!category ? "active" : ""}
                            onClick={() => setPublicCollectionCategory("")}
                        >
                            <strong>All</strong>
                            <span>{entries.length}</span>
                        </button>
                        {categoryCounts.map((item) => (
                            <button
                                type="button"
                                key={item.name}
                                className={category === item.name ? "active" : ""}
                                onClick={() => setPublicCollectionCategory(item.name)}
                            >
                                <strong>{item.name}</strong>
                                <span>{item.count}</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className="research-newsletter-search-row">
                    <label className="research-newsletter-search">
                        <span>General search</span>
                        <input
                            type="search"
                            value={publicCollectionSearch}
                            onChange={(event) => setPublicCollectionSearch(event.target.value)}
                            placeholder="Search title, lodge, source, category..."
                        />
                    </label>
                    <label className="research-newsletter-search category">
                        <span>Category</span>
                        <select
                            value={publicCollectionCategory}
                            onChange={(event) => setPublicCollectionCategory(event.target.value)}
                        >
                            <option value="">All categories</option>
                            {categories.map((item) => (
                                <option key={item} value={item}>{item}</option>
                            ))}
                        </select>
                    </label>
                    {renderResearchDateFilters({
                        from: publicCollectionDateFrom,
                        to: publicCollectionDateTo,
                        sort: publicCollectionSort,
                        onFromChange: setPublicCollectionDateFrom,
                        onToChange: setPublicCollectionDateTo,
                        onSortChange: setPublicCollectionSort,
                        onClear: () => {
                            setPublicCollectionDateFrom("");
                            setPublicCollectionDateTo("");
                        },
                        copy: "Use this when looking for an issue, ceremony text, toast, or source document from a particular period.",
                    })}
                </div>

                {collection.loading && <div className="research-empty">Loading collection entries...</div>}
                {(collection.error || publicCollectionError) && (
                    <div className="research-empty error">{collection.error || publicCollectionError}</div>
                )}

                {!collection.loading && !collection.error && (
                    <div className="research-public-results-head">
                        <div>
                            <strong>{filteredEntries.length}</strong>
                            <span>{hasActiveFilters ? "matching records" : "records in this collection"}</span>
                        </div>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={() => {
                                    setPublicCollectionSearch("");
                                    setPublicCollectionCategory("");
                                    setPublicCollectionDateFrom("");
                                    setPublicCollectionDateTo("");
                                }}
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                )}

                {!collection.loading && !collection.error && (
                    <div className="research-public-document-list refined">
                        {filteredEntries.length === 0 ? (
                            <div className="research-empty">
                                {canManageCollection && Number(data?.totalEntries || 0) > 0
                                    ? "No entries match this filter. Draft entries are visible to archivists; publish selected records to show them publicly."
                                    : data?.empty || entry.archive?.empty || "No published entries are available yet."}
                            </div>
                        ) : filteredEntries.map((item) => (
                            <article className={`research-public-document-card ${item.published === false ? "draft" : ""}`} key={item.id}>
                                <div className="research-public-document-type">
                                    <span>{item.category || item.documentType || "Source"}</span>
                                    {item.fileType && <small>{item.fileType}</small>}
                                </div>
                                <div className="research-public-document-main">
                                    <div className="research-meta-row">
                                        <span className={item.published === false ? "research-newsletter-issue-status draft" : "research-newsletter-issue-status"}>
                                            {item.published === false ? "Draft" : "Published"}
                                        </span>
                                        <span>{item.category}</span>
                                        {item.date && <span>{formatResearchDate(item.date)}</span>}
                                    </div>
                                    <h3>{item.title}</h3>
                                    <p>{item.snippet || item.description || "No summary has been captured yet."}</p>
                                    <div className="research-chip-row">
                                        {item.lodge && <span>{item.lodge}</span>}
                                        {item.orderName && <span>{item.orderName}</span>}
                                        {item.documentType && <span>{item.documentType}</span>}
                                        {item.eventType && <span>{item.eventType}</span>}
                                    </div>
                                </div>
                                <div className="research-newsletter-detail-actions public-document-actions">
                                    {canManageCollection && (
                                        <button
                                            type="button"
                                            onClick={() => updatePublicCollectionPublication(entry, item, item.published === false)}
                                            disabled={publicCollectionSaving}
                                        >
                                            {item.published === false ? "Publish Entry" : "Move to Draft"}
                                        </button>
                                    )}
                                    {item.url && (
                                        <button
                                            type="button"
                                            className="primary"
                                            onClick={() => openCollectionDocument(entry, item)}
                                        >
                                            Open Source
                                        </button>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                <div className="research-newsletter-next">
                    <strong>Refreshing after archive edits</strong>
                    <span>New matching documents are picked up from the database whenever this index is refreshed or reopened. New matches stay in Draft until an archivist publishes them.</span>
                </div>
            </div>
        );
    };

    const renderSurveyQuestionAppendix = (question) => {
        if (question.type === "matrix") {
            return (
                <div className="research-survey-appendix-matrix">
                    {Object.entries(question.matrix || {}).map(([prompt, counts]) => (
                        <div key={prompt}>
                            <strong>{prompt}</strong>
                            <div>
                                {surveyRows(counts).map((row) => (
                                    <span key={row.label}>{row.label}: {row.count}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        const rows = surveyRows(question.counts);
        if (rows.length) {
            return (
                <div className="research-survey-bars">
                    {rows.map((row) => (
                        <div className="research-survey-bar" key={row.label}>
                            <div>
                                <span>{row.label}</span>
                                <strong>{row.count}</strong>
                            </div>
                            <div className="research-survey-track">
                                <span style={{ width: formatSurveyPercent(row.count, question.total) }} />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (question.textResponses?.length) {
            return (
                <ul className="research-survey-text-list">
                    {question.textResponses.slice(0, 40).map((text, index) => (
                        <li key={`${question.id}-${index}`}>{text}</li>
                    ))}
                </ul>
            );
        }

        return <p className="research-muted">No responses recorded for this question.</p>;
    };

    const renderCollectionPlan = (topic) => {
        const plan = topic?.plan;
        if (!plan) return null;

        return (
            <div className="research-plan-panel">
                <p>{plan.summary}</p>
                <div className="research-plan-grid">
                    {plan.sections.map((section) => (
                        <section className="research-plan-card" key={section.title}>
                            <h3>{section.title}</h3>
                            <ul>
                                {section.items.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        );
    };

    const getArchiveTopicMatches = (topic) => {
        const terms = (topic?.archive?.terms || []).map((term) => term.toLowerCase());
        const categories = (topic?.archive?.categories || []).map((category) => category.toLowerCase());
        if (!terms.length && !categories.length) return [];

        const matches = [];
        newsletterIssues.forEach((issue) => {
            (issue.sections || []).forEach((section) => {
                const category = String(section.category || "").trim().toLowerCase();
                const haystack = normalizeSearchText([
                    issue.title,
                    issue.issue,
                    issue.date,
                    issue.description,
                    section.title,
                    section.category,
                    section.snippet,
                    ...(section.tags || []),
                ]);
                const categoryMatch = categories.includes(category);
                const termMatch = terms.some((term) => haystack.includes(term));
                if (categoryMatch || termMatch) {
                    matches.push({ issue, section });
                }
            });
        });

        return matches
            .sort((a, b) => {
                const issueDateA = new Date(a.issue?.dateCreated || a.issue?.date || 0).getTime() || 0;
                const issueDateB = new Date(b.issue?.dateCreated || b.issue?.date || 0).getTime() || 0;
                if (issueDateA !== issueDateB) return issueDateB - issueDateA;
                return Number(a.section?.page || 9999) - Number(b.section?.page || 9999);
            })
            .slice(0, 30);
    };

    const renderArchiveTopic = (topic) => {
        const matches = getArchiveTopicMatches(topic);

        return (
            <div className="research-archive-topic-panel">
                {renderCollectionPlan(topic)}
                <section className="research-archive-topic-results" aria-label={`${topic.title} archive matches`}>
                    <div className="research-archive-topic-heading">
                        <div>
                            <p className="research-eyebrow">Live archive index</p>
                            <h3>Looking East matches</h3>
                        </div>
                        <span>{newsletterLoading ? "..." : matches.length} entries</span>
                    </div>
                    {newsletterLoading && <p className="research-muted">Loading public archive entries...</p>}
                    {newsletterError && <p className="research-empty error">{newsletterError}</p>}
                    {!newsletterLoading && !newsletterError && matches.length === 0 && (
                        <p className="research-muted">{topic.archive?.empty || "No public archive entries have been indexed yet."}</p>
                    )}
                    {!newsletterLoading && !newsletterError && matches.length > 0 && (
                        <div className="research-archive-entry-list">
                            {matches.map(({ issue, section }) => (
                                <article className="research-archive-entry" key={`${issue.id}-${section.sourceKey || section.title}-${section.page}`}>
                                    <div>
                                        <span>{formatPublicIssueLabel(issue)}</span>
                                        <span>{section.category || "Archive entry"}</span>
                                        <span>Page {section.printedPage || section.page || "?"}</span>
                                    </div>
                                    <h4>{section.title}</h4>
                                    {section.snippet && <p>{section.snippet}</p>}
                                    <button type="button" className="research-card-action" onClick={() => openIssuePdf(issue, section.page)}>
                                        Open Source
                                    </button>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        );
    };

    const contributionCategoryFor = (text = "") => {
        const value = String(text).toLowerCase();
        if (/soup|jar|bottle/.test(value)) return "Soup jars";
        if (/clothes|clothing|garment|jersey|shoe|blanket/.test(value)) return "Clothing & warmth";
        if (/food|parcel|hamper|grocer|meal/.test(value)) return "Food parcels";
        if (/book|stationery|school|education|bursar/.test(value)) return "Education support";
        if (/toy|gift|christmas/.test(value)) return "Gifts";
        if (/visit|care|service|volunteer|time/.test(value)) return "Service activity";
        return "Other in-kind support";
    };

    const contributionQuantityFor = (text = "") => {
        const match = String(text).match(/(?:^|\D)(\d{1,5})(?:\D|$)/);
        return match ? Number(match[1]) : null;
    };

    const charityBarOption = (rows, valueKey, title, color = "#2f6c45") => {
        const isNarrow = typeof window !== "undefined" && window.innerWidth < 700;
        const chartRows = rows.slice(0, 8).reverse();
        return {
            title: { text: title, left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
            grid: { top: 42, right: isNarrow ? 14 : 74, bottom: 24, left: 8, containLabel: true },
            xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#64748b" } },
            yAxis: {
                type: "category",
                data: chartRows.map((row) => row.name || row.eventName || row.year || "Unlabelled"),
                axisLabel: { color: "#475569", width: isNarrow ? 92 : 150, overflow: "truncate" },
            },
            series: [{
                type: "bar",
                data: chartRows.map((row) => Number(row[valueKey] || 0)),
                itemStyle: { color, borderRadius: [0, 5, 5, 0] },
                label: { show: true, position: isNarrow ? "insideRight" : "right" },
                labelLayout: { hideOverlap: true },
            }],
        };
    };

    const charityCashOption = (rows) => {
        const isNarrow = typeof window !== "undefined" && window.innerWidth < 700;
        const chartRows = rows.filter((row) => Number(row.cashCents || 0) > 0).slice(0, 8).reverse();
        return {
            title: { text: "Recorded cash contributions", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: (value) => formatResearchCurrency(Number(value || 0)) },
            grid: { top: 42, right: isNarrow ? 14 : 118, bottom: 24, left: 8, containLabel: true },
            xAxis: { type: "value", axisLabel: { color: "#64748b", formatter: (value) => `R ${Math.round(Number(value || 0) / 100).toLocaleString("en-ZA")}` } },
            yAxis: {
                type: "category",
                data: chartRows.map((row) => row.name || row.eventName || row.year || "Unlabelled"),
                axisLabel: { color: "#475569", width: isNarrow ? 92 : 150, overflow: "truncate" },
            },
            series: [{
                type: "bar",
                data: chartRows.map((row) => Number(row.cashCents || 0)),
                itemStyle: { color: "#245f7a", borderRadius: [0, 5, 5, 0] },
                label: { show: true, position: isNarrow ? "insideRight" : "right", formatter: ({ value }) => formatResearchCurrency(value) },
                labelLayout: { hideOverlap: true },
            }],
        };
    };

    const charityDonutOption = (rows, title, colorSet = ["#2f6c45", "#245f7a", "#8b6f2f", "#6f4a8e", "#5d6f86", "#9a6f3b"]) => ({
        title: { text: title, left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
        tooltip: { trigger: "item" },
        legend: { bottom: 0, type: "scroll", textStyle: { color: "#475569" } },
        color: colorSet,
        series: [{
            type: "pie",
            radius: ["42%", "68%"],
            center: ["50%", "46%"],
            data: rows.slice(0, 8).map((row) => ({
                name: row.name || row.category || row.documentType || "Unlabelled",
                value: Number(row.records || row.count || 0),
            })),
            label: { formatter: "{b}: {c}", overflow: "truncate", width: 140 },
            labelLayout: { hideOverlap: true },
            itemStyle: { borderColor: "#fff", borderWidth: 2 },
        }],
    });

    const renderCharityFundraising = (topic) => {
        const data = charityFundraising;
        const records = Array.isArray(data?.records) ? data.records : [];
        const sourceDocuments = Array.isArray(data?.sourceDocuments) ? data.sourceDocuments : [];
        const sourceByLodge = data?.crossReferences?.sourceByLodge || [];
        const sourceByEvent = data?.crossReferences?.sourceByEvent || [];
        const canManageCharity = Boolean(data?.canManage || canEditPublicCollections);
        const canReviewCharity = Boolean(data?.canReview || canManageCharity);
        const query = charityFundraisingSearch.trim().toLowerCase();
        const lodgeFilter = charityFundraisingLodge.trim();
        const category = charityFundraisingCategory;
        const hasCharityDateFilter = Boolean(charityFundraisingDateFrom || charityFundraisingDateTo);
        const lodgeOptions = [...new Set([
            ...records.flatMap((record) => (record.lodges || []).map((lodge) => lodge.name).filter(Boolean)),
            ...sourceDocuments.map((item) => item.lodge).filter(Boolean),
        ])].sort((a, b) => a.localeCompare(b));
        const matchesSelectedLodge = (names = []) => !lodgeFilter || names.includes(lodgeFilter);
        const activityCategoryRows = Object.values(records.reduce((acc, record) => {
            const key = record.category || "Charity";
            if (!acc[key]) acc[key] = { name: key, records: 0, cashCents: 0 };
            acc[key].records += 1;
            acc[key].cashCents += Number(record.amountCents || 0);
            return acc;
        }, {})).sort((a, b) => b.records - a.records || a.name.localeCompare(b.name));
        const sourceCategoryRows = Object.entries(data?.categoryCounts || {}).map(([name, count]) => ({
            name,
            records: Number(count || 0),
        })).sort((a, b) => b.records - a.records || a.name.localeCompare(b.name));
        const categoryCounts = [
            { key: "all", label: "All records", count: records.length },
            { key: "cash", label: "Cash", count: records.filter((record) => Number(record.amountCents || 0) > 0).length },
            { key: "non-cash", label: "Non-cash", count: records.filter((record) => Number(record.amountCents || 0) <= 0 && record.contribution).length },
            { key: "evidence", label: "With evidence", count: records.filter((record) => Number(record.evidenceCounts?.total || 0) > 0).length },
            ...activityCategoryRows.map((item) => ({ key: `activity:${item.name}`, label: item.name, count: item.records })),
            ...sourceCategoryRows.map((item) => ({ key: `source:${item.name}`, label: item.name, count: item.records })),
        ];
        const filteredRecords = records.filter((record) => {
            if (category === "cash" && Number(record.amountCents || 0) <= 0) return false;
            if (category === "non-cash" && !(Number(record.amountCents || 0) <= 0 && record.contribution)) return false;
            if (category === "evidence" && Number(record.evidenceCounts?.total || 0) <= 0) return false;
            if (category.startsWith("activity:") && record.category !== category.slice("activity:".length)) return false;
            if (category.startsWith("source:")) return false;
            if (!matchesSelectedLodge((record.lodges || []).map((lodge) => lodge.name))) return false;
            if (!dateInResearchRange(record.date, charityFundraisingDateFrom, charityFundraisingDateTo)) return false;
            const haystack = normalizeSearchText([
                record.beneficiary,
                record.category,
                record.description,
                record.eventName,
                record.venue,
                record.contribution,
                record.outcome,
                record.publicNotes,
                record.date,
                ...(record.lodges || []).map((lodge) => lodge.name),
                ...(record.evidence || []).map((source) => `${source.title} ${source.description}`),
            ]);
            return !query || haystack.includes(query);
        }).sort((a, b) => compareResearchDateValues(a.date, b.date, charityFundraisingSort));
        const matchingSources = sourceDocuments.filter((item) => {
            if (["cash", "non-cash", "evidence"].includes(category) || category.startsWith("activity:")) return false;
            if (category.startsWith("source:") && item.category !== category.slice("source:".length)) return false;
            if (!dateInResearchRange(item.date, charityFundraisingDateFrom, charityFundraisingDateTo)) return false;
            const haystack = normalizeSearchText([
                item.title,
                item.description,
                item.snippet,
                item.lodge,
                item.category,
                item.documentType,
                item.eventType,
                item.filename,
            ]);
            if (!matchesSelectedLodge([item.lodge].filter(Boolean))) return false;
            return !query || haystack.includes(query);
        }).sort((a, b) => compareResearchDateValues(a.date, b.date, charityFundraisingSort));
        const sourceSearchText = (item = {}) => normalizeSearchText([
            item.title,
            item.description,
            item.snippet,
            item.lodge,
            item.category,
            item.documentType,
            item.eventType,
            item.filename,
        ]);
        const sourceLooksLikeAdvert = (item = {}) => {
            const text = sourceSearchText(item);
            return [
                "advert",
                "notice",
                "appeal",
                "fundraiser",
                "fundraising",
                "golf day",
                "soup drive",
                "family day",
                "pre-order",
                "bowtie",
                "ticket",
            ].some((term) => text.includes(term));
        };
        const charityAdvertSources = matchingSources.filter(sourceLooksLikeAdvert);
        const sourceByLodgeVisible = Object.values(matchingSources.reduce((acc, entry) => {
            const key = entry.lodge || "Unlinked source records";
            if (!acc[key]) acc[key] = { name: key, records: 0, adverts: 0, drafts: 0, latestDate: null };
            acc[key].records += 1;
            if (/advert/i.test(entry.documentType || entry.category || entry.title || "")) acc[key].adverts += 1;
            if (entry.published === false) acc[key].drafts += 1;
            if (entry.date && (!acc[key].latestDate || new Date(entry.date) > new Date(acc[key].latestDate))) {
                acc[key].latestDate = entry.date;
            }
            return acc;
        }, {})).sort((a, b) => b.records - a.records || a.name.localeCompare(b.name));
        const sourceByEventVisible = Object.values(matchingSources.reduce((acc, entry) => {
            const key = entry.eventType || "Archive source";
            if (!acc[key]) acc[key] = { eventName: key, records: 0, adverts: 0, lodges: new Set(), drafts: 0 };
            acc[key].records += 1;
            if (/advert/i.test(entry.documentType || entry.category || entry.title || "")) acc[key].adverts += 1;
            if (entry.lodge) acc[key].lodges.add(entry.lodge);
            if (entry.published === false) acc[key].drafts += 1;
            return acc;
        }, {})).map((row) => ({ ...row, lodges: row.lodges.size })).sort((a, b) => b.records - a.records || a.eventName.localeCompare(b.eventName));
        const sourceByDocumentTypeVisible = Object.values(matchingSources.reduce((acc, entry) => {
            const key = entry.documentType || "Document";
            if (!acc[key]) acc[key] = { documentType: key, records: 0, drafts: 0 };
            acc[key].records += 1;
            if (entry.published === false) acc[key].drafts += 1;
            return acc;
        }, {})).sort((a, b) => b.records - a.records || a.documentType.localeCompare(b.documentType));
        const eventSourceMatches = (source, eventName) => {
            const eventText = normalizeSearchText([eventName]);
            const sourceText = sourceSearchText(source);
            if (!eventText) return false;
            if (sourceText.includes(eventText)) return true;
            const ignored = new Set(["and", "the", "for", "with", "from", "club", "country", "charity", "event"]);
            const tokens = eventText
                .split(/[^a-z0-9]+/i)
                .filter((token) => token.length > 3 && !ignored.has(token));
            return tokens.length > 0 && tokens.some((token) => sourceText.includes(token));
        };
        const eventSourcesFor = (eventName) => charityAdvertSources
            .filter((source) => eventSourceMatches(source, eventName))
            .slice(0, 4);
        const filteredByLodge = Object.values(filteredRecords.reduce((acc, record) => {
            const lodges = record.lodges.length ? record.lodges : [{ id: "unlinked", name: "Unlinked records", code: "" }];
            lodges.forEach((lodge) => {
                const key = lodge.id || lodge.name;
                if (!acc[key]) acc[key] = { id: key, code: lodge.code || "", name: lodge.name || "Unlinked records", records: 0, cashCents: 0, evidence: 0, beneficiaries: new Set() };
                acc[key].records += 1;
                acc[key].cashCents += Number(record.amountCents || 0);
                acc[key].evidence += Number(record.evidenceCounts?.total || 0);
                acc[key].beneficiaries.add(record.beneficiary);
            });
            return acc;
        }, {})).map((row) => ({ ...row, beneficiaries: row.beneficiaries.size })).sort((a, b) => b.records - a.records || a.name.localeCompare(b.name));
        const filteredByBeneficiary = filteredRecords
            .map((record) => ({
                id: record.id,
                name: record.beneficiary,
                category: record.category,
                records: 1,
                cashCents: record.amountCents,
                contribution: record.contribution,
                eventName: record.eventName,
                date: record.date,
                evidence: record.evidenceCounts?.total || 0,
                lodges: record.lodges.map((lodge) => lodge.name),
            }))
            .sort((a, b) => b.cashCents - a.cashCents || a.name.localeCompare(b.name));
        const filteredByEvent = Object.values(filteredRecords.reduce((acc, record) => {
            const key = record.eventName || "Charity activity";
            if (!acc[key]) acc[key] = { eventName: key, records: 0, cashCents: 0, evidence: 0, beneficiaries: new Set(), lodges: new Set() };
            acc[key].records += 1;
            acc[key].cashCents += Number(record.amountCents || 0);
            acc[key].evidence += Number(record.evidenceCounts?.total || 0);
            acc[key].beneficiaries.add(record.beneficiary);
            record.lodges.forEach((lodge) => acc[key].lodges.add(lodge.name));
            return acc;
        }, {})).map((row) => ({ ...row, beneficiaries: row.beneficiaries.size, lodges: row.lodges.size })).sort((a, b) => b.records - a.records || a.eventName.localeCompare(b.eventName));
        const filteredByYear = Object.values(filteredRecords.reduce((acc, record) => {
            const year = record.date ? new Date(record.date).getFullYear() : "Undated";
            const key = Number.isFinite(year) ? String(year) : "Undated";
            if (!acc[key]) acc[key] = { year: key, records: 0, cashCents: 0, evidence: 0 };
            acc[key].records += 1;
            acc[key].cashCents += Number(record.amountCents || 0);
            acc[key].evidence += Number(record.evidenceCounts?.total || 0);
            return acc;
        }, {})).sort((a, b) => String(a.year).localeCompare(String(b.year)));
        const filteredActivityCategories = Object.values(filteredRecords.reduce((acc, record) => {
            const key = record.category || "Charity";
            if (!acc[key]) acc[key] = { name: key, records: 0, cashCents: 0 };
            acc[key].records += 1;
            acc[key].cashCents += Number(record.amountCents || 0);
            return acc;
        }, {})).sort((a, b) => b.records - a.records || a.name.localeCompare(b.name));
        const filteredInKindRows = Object.values(filteredRecords.reduce((acc, record) => {
            const contribution = String(record.contribution || "").trim();
            if (!contribution || Number(record.amountCents || 0) > 0) return acc;
            const name = contributionCategoryFor(contribution);
            if (!acc[name]) acc[name] = { name, records: 0, quantity: 0, examples: new Set() };
            const quantity = contributionQuantityFor(contribution);
            acc[name].records += 1;
            acc[name].quantity += quantity || 0;
            acc[name].examples.add(contribution);
            return acc;
        }, {})).map((row) => ({
            ...row,
            examples: [...row.examples].slice(0, 2),
        })).sort((a, b) => b.records - a.records || b.quantity - a.quantity || a.name.localeCompare(b.name));
        const visibleSourceCategories = Object.values(matchingSources.reduce((acc, source) => {
            const key = source.category || "Source";
            if (!acc[key]) acc[key] = { name: key, records: 0 };
            acc[key].records += 1;
            return acc;
        }, {})).sort((a, b) => b.records - a.records || a.name.localeCompare(b.name));
	        const eventRows = [
	            ...filteredByEvent.map((item) => ({ ...item, sourceOnly: false })),
	            ...(query || lodgeFilter || hasCharityDateFilter ? sourceByEventVisible : sourceByEvent)
	                .filter((sourceEvent) => !filteredByEvent.some((item) => item.eventName === sourceEvent.eventName))
                .map((item) => ({
                    ...item,
                    sourceOnly: true,
                    beneficiaries: 0,
                    cashCents: 0,
	                })),
	        ].sort((a, b) => b.records - a.records || a.eventName.localeCompare(b.eventName));
        const filteredBeneficiaries = new Set(filteredRecords.map((record) => record.beneficiary).filter(Boolean)).size;
        const filteredCashCents = filteredRecords.reduce((sum, record) => sum + Number(record.amountCents || 0), 0);
        const filteredNonCashRecords = filteredRecords.filter((record) => Number(record.amountCents || 0) <= 0 && record.contribution).length;
        const filteredNonCashQuantity = filteredInKindRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
        const leadingLodge = filteredByLodge[0];
        const leadingEvent = filteredByEvent[0];
        const leadingCategory = filteredActivityCategories[0] || visibleSourceCategories[0];
        const latestSource = matchingSources
            .filter((source) => source.date)
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        const featuredEvidenceSources = [...matchingSources]
            .sort((a, b) => {
                const scoreFor = (item) => {
                    let score = 0;
                    if (item.thumbnailUrl) score += 6;
                    if (sourceLooksLikeAdvert(item)) score += 5;
                    if (/acknowledg|thank you|report/i.test(`${item.category || ""} ${item.documentType || ""} ${item.title || ""}`)) score += 4;
                    if (item.published !== false) score += 2;
                    return score;
                };
                return scoreFor(b) - scoreFor(a) || compareResearchDateValues(a.date, b.date, "newest");
            })
            .slice(0, 4);
	        const isCharitySectionCollapsed = (key) => Boolean(charityCollapsedSections[key]);
	        const toggleCharitySection = (key) => {
	            setCharityCollapsedSections((state) => ({ ...state, [key]: !state[key] }));
	        };
        const charitySectionHeader = (key, title, meta) => (
            <button
                type="button"
                className="research-charity-section-toggle"
                onClick={() => toggleCharitySection(key)}
                aria-expanded={!isCharitySectionCollapsed(key)}
            >
                <span>{title}</span>
                <em>{meta}</em>
                <strong>{isCharitySectionCollapsed(key) ? "Show" : "Hide"}</strong>
            </button>
        );
        const renderCharitySourcePreview = (item, compact = false) => (
            <div className={`research-charity-source-preview${compact ? " compact" : ""}${item.thumbnailUrl ? "" : " no-preview"}`}>
                {item.thumbnailUrl && (
                    <img
                        src={item.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                            event.currentTarget.style.display = "none";
                            event.currentTarget.parentElement?.classList.add("no-preview");
                        }}
                    />
                )}
                <span>
                    <strong>{item.category || item.documentType || "Archive evidence"}</strong>
                    {item.fileType && <em>{item.fileType}</em>}
                </span>
            </div>
        );
        const renderCharitySourceCard = (item, featured = false) => (
            <article
                className={`research-charity-source-card${featured ? " featured" : ""}${item.published === false ? " draft" : ""}`}
                key={item.id}
            >
                <button type="button" className="research-charity-source-open" onClick={() => openCollectionDocument(topic, item)}>
                    {renderCharitySourcePreview(item, !featured)}
                </button>
                <div className="research-charity-source-content">
                    <div className="research-meta-row">
                        <span className={item.published === false ? "research-newsletter-issue-status draft" : "research-newsletter-issue-status"}>
                            {item.published === false ? "Draft" : "Published"}
                        </span>
                        {item.date && <span>{formatResearchDate(item.date)}</span>}
                        {item.lodge && <span>{item.lodge}</span>}
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.snippet || item.description || "No summary has been captured yet."}</p>
                    <div className="research-charity-source-footer">
                        <span>{item.category || item.documentType || "Source"}</span>
                        {item.eventType && <span>{item.eventType}</span>}
                    </div>
                    <div className="research-newsletter-detail-actions public-document-actions">
                        {canManageCharity && (
                            <button
                                type="button"
                                onClick={() => updatePublicCollectionPublication(topic, item, item.published === false)}
                                disabled={publicCollectionSaving}
                            >
                                {item.published === false ? "Publish Entry" : "Move to Draft"}
                            </button>
                        )}
                        {item.url && (
                            <button type="button" className="primary" onClick={() => openCollectionDocument(topic, item)}>
                                Open Source
                            </button>
                        )}
                    </div>
                </div>
            </article>
        );

        return (
            <div className="research-charity-dashboard">
                {charityFundraisingLoading && <p className="research-muted">Loading charity and fundraising records...</p>}
                {charityFundraisingError && <p className="research-empty error">{charityFundraisingError}</p>}
                {!charityFundraisingLoading && !charityFundraisingError && data && (
                    <>
                        <section className="research-public-collection-hero charity">
                            <div className="research-public-collection-icon" aria-hidden="true">
                                <img src={topic.icon} alt="" />
                            </div>
                            <div className="research-public-collection-copy">
                                <p className="research-eyebrow">Public charity research</p>
                                <h3>{data.title || topic.title}</h3>
                                <p>{data.description || topic.detail}</p>
                                <div className="research-public-collection-tags">
                                    <span>Resources records + archive evidence</span>
                                    <span>{records.length} activity records</span>
                                    <span>{sourceDocuments.length} source documents</span>
                                </div>
                                {canManageCharity && (
                                    <div className="research-newsletter-admin-actions">
                                        <button
                                            type="button"
                                            className="research-refresh-button"
                                            onClick={() => loadCharityFundraising(true)}
                                            disabled={charityFundraisingLoading}
                                        >
                                            {charityFundraisingLoading ? "Refreshing..." : "Refresh Index"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="research-lodge-summary charity" aria-label="Charity and fundraising summary">
                            <div>
                                <strong>{Number(data.totals?.charityRecords || 0).toLocaleString()}</strong>
                                <span>activity records</span>
                            </div>
                            <div>
                                <strong>{Number(data.totals?.beneficiaries || 0).toLocaleString()}</strong>
                                <span>beneficiaries</span>
                            </div>
                            <div>
                                <strong>{formatResearchCurrency(data.totals?.cashCents || 0)}</strong>
                                <span>recorded cash</span>
                            </div>
                            <div>
                                <strong>{Number(data.totals?.nonCashRecords || 0).toLocaleString()}</strong>
                                <span>non-cash records</span>
                            </div>
	                            <div>
	                                <strong>{Number(data.totals?.evidenceDocuments || 0).toLocaleString()}</strong>
	                                <span>linked evidence</span>
	                            </div>
	                            <div>
	                                <strong>{Number(data.totals?.sourceAdverts || 0).toLocaleString()}</strong>
	                                <span>archive adverts</span>
	                            </div>
		                            {canReviewCharity && (
		                                <div>
		                                    <strong>{Number(data.totals?.hiddenDraftSources || 0).toLocaleString()}</strong>
	                                    <span>draft source records</span>
	                                </div>
                            )}
                        </section>

	                        <section className="research-charity-search" aria-label="Search charity and fundraising">
	                            <label htmlFor="charity-research-search">
	                                Cross-reference search
	                                <input
	                                    id="charity-research-search"
                                    type="search"
                                    value={charityFundraisingSearch}
                                    onChange={(event) => setCharityFundraisingSearch(event.target.value)}
	                                    placeholder="Search lodge, beneficiary, event, contribution, source, outcome..."
	                                />
	                            </label>
	                            <label htmlFor="charity-lodge-filter">
	                                Lodge / body
	                                <select
	                                    id="charity-lodge-filter"
	                                    value={charityFundraisingLodge}
	                                    onChange={(event) => setCharityFundraisingLodge(event.target.value)}
	                                >
	                                    <option value="">All lodges and bodies</option>
	                                    {lodgeOptions.map((name) => (
	                                        <option value={name} key={name}>{name}</option>
	                                    ))}
	                                </select>
	                            </label>
                                {renderResearchDateFilters({
                                    from: charityFundraisingDateFrom,
                                    to: charityFundraisingDateTo,
                                    sort: charityFundraisingSort,
                                    onFromChange: setCharityFundraisingDateFrom,
                                    onToChange: setCharityFundraisingDateTo,
                                    onSortChange: setCharityFundraisingSort,
                                    onClear: () => {
                                        setCharityFundraisingDateFrom("");
                                        setCharityFundraisingDateTo("");
                                    },
                                    copy: "Filter fundraising activity, acknowledgements, adverts, and linked evidence by date.",
                                })}
	                            {(query || lodgeFilter || category !== "all" || hasCharityDateFilter) && (
	                                <button
	                                    type="button"
	                                    onClick={() => {
	                                        setCharityFundraisingSearch("");
	                                        setCharityFundraisingLodge("");
                                            setCharityFundraisingCategory("all");
                                            setCharityFundraisingDateFrom("");
                                            setCharityFundraisingDateTo("");
	                                    }}
	                                >
	                                    Clear
	                                </button>
	                            )}
                            <p>
                                Search across lodge names, beneficiary names, events, public outcomes, contributions, and linked archive evidence. Contact details remain in Resources, not on this public research page.
                            </p>
                        </section>

                        <section className="research-charity-filter-strip" aria-label="Charity filters">
                            {categoryCounts.map((item) => (
                                <button
                                    type="button"
                                    key={item.key}
                                    className={category === item.key ? "active" : ""}
                                    onClick={() => setCharityFundraisingCategory(item.key)}
                                    disabled={item.key !== "all" && !item.count}
                                >
                                    <strong>{item.count}</strong>
                                    <span>{item.label}</span>
                                </button>
                            ))}
                        </section>

                        <section className="research-charity-insights" aria-label="Charity research highlights">
                            <article>
                                <span>Leading lodge/body</span>
                                <strong>{leadingLodge?.name || "Not enough data"}</strong>
                                <em>{leadingLodge ? `${leadingLodge.records} records · ${leadingLodge.beneficiaries || 0} beneficiaries` : "Add or publish linked records"}</em>
                            </article>
                            <article>
                                <span>Leading event</span>
                                <strong>{leadingEvent?.eventName || "Not enough data"}</strong>
                                <em>{leadingEvent ? `${leadingEvent.records} records · ${formatResearchCurrency(leadingEvent.cashCents)}` : "Add event names on charity records"}</em>
                            </article>
                            <article>
                                <span>Strongest category</span>
                                <strong>{leadingCategory?.name || "Not enough data"}</strong>
                                <em>{leadingCategory ? `${leadingCategory.records} matching records` : "No category split yet"}</em>
                            </article>
                            <article>
                                <span>Latest source</span>
                                <strong>{latestSource?.lodge || latestSource?.category || "No source date"}</strong>
                                <em>{latestSource ? `${formatResearchDate(latestSource.date)} · ${latestSource.title}` : "No matching source document"}</em>
                            </article>
                        </section>

                        {featuredEvidenceSources.length > 0 && (
                            <section className="research-charity-evidence-showcase" aria-label="Featured charity evidence">
                                <div className="research-section-heading-row">
                                    <div>
                                        <p className="research-eyebrow">Evidence desk</p>
                                        <h3>Adverts, letters and acknowledgements</h3>
                                    </div>
                                    <span>{featuredEvidenceSources.length} featured</span>
                                </div>
                                <div className="research-charity-featured-sources">
                                    {featuredEvidenceSources.map((item) => renderCharitySourceCard(item, true))}
                                </div>
                            </section>
                        )}

		                        <section className="research-charity-section" aria-label="Charity analytics">
		                            {charitySectionHeader("analytics", "Analytics", "Charts and high-level summaries")}
		                            {!isCharitySectionCollapsed("analytics") && (
		                                <>
		                                    <div className="research-charity-chart-totals" aria-label="Current charity filter totals">
		                                        <div>
		                                            <strong>{filteredRecords.length.toLocaleString()}</strong>
		                                            <span>activity total</span>
		                                        </div>
		                                        <div>
		                                            <strong>{filteredBeneficiaries.toLocaleString()}</strong>
		                                            <span>beneficiaries</span>
		                                        </div>
		                                        <div>
		                                            <strong>{formatResearchCurrency(filteredCashCents)}</strong>
		                                            <span>cash total</span>
		                                        </div>
                                        <div>
                                            <strong>{filteredNonCashRecords.toLocaleString()}</strong>
                                            <span>non-cash records</span>
                                        </div>
                                        <div>
                                            <strong>{filteredNonCashQuantity ? filteredNonCashQuantity.toLocaleString() : "Grouped"}</strong>
                                            <span>in-kind quantity</span>
                                        </div>
                                        <div>
                                            <strong>{matchingSources.length.toLocaleString()}</strong>
                                            <span>source records</span>
                                        </div>
		                                        <div>
		                                            <strong>{charityAdvertSources.length.toLocaleString()}</strong>
		                                            <span>adverts/notices</span>
		                                        </div>
		                                    </div>
		                                    <div className="research-charity-charts">
		                                        <article>
		                                            <ReactECharts option={charityBarOption(filteredByLodge, "records", "Activity by lodge")} style={{ height: 300 }} />
		                                        </article>
		                                        <article>
		                                            <ReactECharts option={charityCashOption(filteredByBeneficiary)} style={{ height: 300 }} />
		                                        </article>
                                        <article>
                                            <ReactECharts option={charityBarOption(filteredByEvent, "records", "Activity by event", "#8b6f2f")} style={{ height: 300 }} />
                                        </article>
                                        <article>
                                            <ReactECharts option={charityBarOption(filteredInKindRows, "quantity", "In-kind contribution quantities", "#2f6f73")} style={{ height: 300 }} />
                                        </article>
                                        <article>
                                            <ReactECharts option={charityBarOption(filteredByYear, "records", "Activity by year", "#6f4a8e")} style={{ height: 300 }} />
                                        </article>
                                                <article>
                                                    <ReactECharts option={charityDonutOption(filteredActivityCategories, "Activity categories")} style={{ height: 320 }} />
                                                </article>
		                                        <article>
                                            <ReactECharts option={charityDonutOption(visibleSourceCategories, "Archive source categories", ["#245f7a", "#2f6c45", "#8b6f2f", "#6f4a8e"])} style={{ height: 320 }} />
                                        </article>
                                    </div>
                                    {filteredInKindRows.length > 0 && (
                                        <div className="research-charity-in-kind" aria-label="In-kind contribution summary">
                                            {filteredInKindRows.slice(0, 6).map((item) => (
                                                <article key={item.name}>
                                                    <span>{item.name}</span>
                                                    <strong>
                                                        {item.quantity ? item.quantity.toLocaleString() : item.records.toLocaleString()}
                                                    </strong>
                                                    <em>
                                                        {item.quantity ? `${item.records} record${item.records === 1 ? "" : "s"}` : "recorded item"}
                                                    </em>
                                                    {item.examples.length > 0 && <p>{item.examples.join("; ")}</p>}
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </section>

	                        <section className="research-charity-section" aria-label="Charity cross references">
	                            {charitySectionHeader("crossrefs", "Cross-References", `${matchingSources.length} source records · ${filteredRecords.length} activity records`)}
	                            {!isCharitySectionCollapsed("crossrefs") && (
	                                <div className="research-charity-crossrefs">
	                            <article>
	                                <h3>Lodges & Bodies</h3>
	                                {[...filteredByLodge, ...(query || lodgeFilter || hasCharityDateFilter || category.startsWith("source:") ? sourceByLodgeVisible : sourceByLodge)
	                                    .filter((sourceLodge) => !filteredByLodge.some((item) => item.name === sourceLodge.name))
	                                    .map((sourceLodge) => ({ ...sourceLodge, sourceOnly: true }))]
	                                    .slice(0, 8)
	                                    .map((item) => (
	                                    <div className="research-charity-crossref-item" key={item.id || item.name}>
	                                        <span>{item.name}</span>
	                                        <strong>{item.records}</strong>
	                                        <em>
	                                            {item.sourceOnly
	                                                ? `${item.adverts || 0} adverts · ${item.drafts || 0} draft source records`
	                                                : `${item.beneficiaries} beneficiaries · ${formatResearchCurrency(item.cashCents)}`}
	                                        </em>
	                                    </div>
	                                ))}
                            </article>
                            <article>
                                <h3>Beneficiaries</h3>
                                {filteredByBeneficiary.slice(0, 8).map((item) => (
                                    <div className="research-charity-crossref-item" key={item.id}>
                                        <span>{item.name}</span>
                                        <strong>{formatResearchCurrency(item.cashCents)}</strong>
                                        <em>{item.contribution || item.eventName}</em>
                                    </div>
                                ))}
                            </article>
	                            <article>
	                                <h3>Events</h3>
	                                {eventRows.slice(0, 8).map((item) => {
	                                    const eventSources = eventSourcesFor(item.eventName);
	                                    return (
	                                        <div className="research-charity-crossref-item" key={item.eventName}>
	                                            <span>{item.eventName}</span>
	                                            <strong>{item.records}</strong>
	                                            <em>
	                                                {item.sourceOnly
	                                                    ? `${item.adverts || 0} adverts · ${item.lodges || 0} lodges · source records`
	                                                    : `${item.beneficiaries} beneficiaries · ${item.lodges} lodges · ${eventSources.length} advert/source records`}
	                                            </em>
	                                            {eventSources.length > 0 && (
	                                                <div className="research-charity-crossref-sources">
                                                    {eventSources.map((source) => (
                                                        <button type="button" key={source.id} onClick={() => openCollectionDocument(topic, source)}>
                                                            <small>{source.published === false ? "Draft" : source.category || "Source"}</small>
                                                            <span>{source.title}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </article>
	                            <article>
	                                <h3>Archive Evidence</h3>
		                                {sourceByDocumentTypeVisible.length > 0 && (
		                                    <div className="research-charity-source-types" aria-label="Charity source document types">
		                                        {sourceByDocumentTypeVisible.slice(0, 4).map((item) => (
		                                            <span key={item.documentType}>{item.records} {item.documentType}</span>
		                                        ))}
		                                    </div>
		                                )}
	                                {charityAdvertSources.length === 0 ? (
	                                    <p className="research-muted">No advert or notice source records match this search.</p>
	                                ) : charityAdvertSources.slice(0, 8).map((source) => (
                                    <button
                                        type="button"
                                        className="research-charity-advert-button"
                                        key={source.id}
                                        onClick={() => openCollectionDocument(topic, source)}
                                    >
                                        <small>{source.published === false ? "Draft" : source.category || source.documentType || "Source"}</small>
                                        <span>{source.title}</span>
                                        <em>{[source.lodge, formatResearchDate(source.date)].filter(Boolean).join(" · ")}</em>
                                    </button>
	                                ))}
	                            </article>
	                                </div>
	                            )}
	                        </section>

	                        <section className="research-charity-section" aria-label="Charity activity records">
	                            {charitySectionHeader("records", "Activity Records", `${filteredRecords.length} matching activity records`)}
	                            {!isCharitySectionCollapsed("records") && (
	                            <div className="research-charity-records">
	                            <div className="research-public-results-head">
	                                <div>
                                    <strong>{filteredRecords.length}</strong>
                                    <span>matching activity records</span>
                                </div>
	                                {(query || lodgeFilter || category !== "all" || hasCharityDateFilter) && (
                                    <button
                                        type="button"
	                                        onClick={() => {
	                                            setCharityFundraisingSearch("");
	                                            setCharityFundraisingCategory("all");
	                                            setCharityFundraisingLodge("");
                                                setCharityFundraisingDateFrom("");
                                                setCharityFundraisingDateTo("");
	                                        }}
	                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                            {filteredRecords.length === 0 ? (
                                <p className="research-muted">No charity records match this search.</p>
                            ) : filteredRecords.map((record) => (
                                <article className="research-charity-record-card" key={record.id}>
                                    <div className="research-meta-row">
                                        <span>{record.category || "Charity"}</span>
                                        <span>{record.status}</span>
                                        <span>{formatResearchDate(record.date) || "Undated"}</span>
                                    </div>
                                    <h3>{record.beneficiary}</h3>
                                    <p>{record.description || record.outcome || "No public summary has been captured yet."}</p>
                                    <div className="research-charity-record-facts">
                                        <div><span>Event</span><strong>{record.eventName}</strong></div>
                                        <div><span>Contribution</span><strong>{record.contribution || formatResearchCurrency(record.amountCents)}</strong></div>
                                        <div><span>Lodge / Entity</span><strong>{(record.lodges || []).map((lodge) => lodge.name).join(", ") || "Unlinked"}</strong></div>
                                        <div><span>Evidence</span><strong>{record.evidenceCounts?.total || 0}</strong></div>
                                    </div>
                                    {record.outcome && <p className="research-charity-outcome">{record.outcome}</p>}
                                    {record.evidence?.length > 0 && (
                                        <div className="research-charity-source-row">
                                            {record.evidence.map((source) => (
                                                <button type="button" key={source.id} onClick={() => openCollectionDocument(topic, source)}>
                                                    <span>{source.evidenceType}</span>
                                                    <strong>{source.title}</strong>
                                                </button>
                                            ))}
                                        </div>
                                    )}
	                                </article>
	                            ))}
	                            </div>
	                            )}
	                        </section>

	                        <section className="research-charity-section research-charity-sources" aria-label="Charity source documents">
	                            {charitySectionHeader("sources", "Archive Source Documents", `${matchingSources.length} matching source records`)}
	                            {!isCharitySectionCollapsed("sources") && (
	                            <>
	                            <div className="research-archive-topic-heading">
	                                <div>
                                    <p className="research-eyebrow">Archive evidence</p>
                                    <h3>Matching source documents</h3>
                                </div>
                                <span>{matchingSources.length} records</span>
                            </div>
                            <div className="research-public-document-list refined">
                                {matchingSources.length === 0 ? (
                                    <p className="research-muted">No source documents match this search.</p>
                                ) : matchingSources.slice(0, 36).map((item) => renderCharitySourceCard(item))}
	                            </div>
	                            </>
	                            )}
	                        </section>
                    </>
                )}
            </div>
        );
    };

    const renderCharityCase = (topic) => {
        const charityCase = topic?.charityCase;
        if (!charityCase) return null;

        return (
            <div className="research-charity-case">
                <section>
                    <h3>Public Summary</h3>
                    <p>{charityCase.summary}</p>
                </section>
                <section>
                    <h3>Recorded Beneficiaries</h3>
                    <div className="research-charity-table" role="table" aria-label={`${topic.title} beneficiary records`}>
                        <div role="row">
                            <span role="columnheader">Beneficiary</span>
                            <span role="columnheader">Date</span>
                            <span role="columnheader">Contribution</span>
                            <span role="columnheader">Event</span>
                            <span role="columnheader">Public note</span>
                        </div>
                        {charityCase.records.map((record) => (
                            <div role="row" key={`${record.beneficiary}-${record.event}`}>
                                <span role="cell" data-label="Beneficiary">{record.beneficiary}</span>
                                <span role="cell" data-label="Date">{record.date}</span>
                                <span role="cell" data-label="Contribution">{record.contribution}</span>
                                <span role="cell" data-label="Event">{record.event}</span>
                                <span role="cell" data-label="Public note">{record.note}</span>
                            </div>
                        ))}
                    </div>
                </section>
                <section>
                    <h3>Source Notes</h3>
                    <div className="research-charity-evidence">
                        {charityCase.evidence.map((source) => (
                            <article key={source.source}>
                                <strong>{source.source}</strong>
                                <p>{source.note}</p>
                            </article>
                        ))}
                    </div>
                </section>
            </div>
        );
    };

    const renderLodgeHeritage = (topic) => {
        const lodges = lodgeHeritage?.lodges || [];
        const canManageLodgeHeritage = Boolean(lodgeHeritage?.canManage || canEditPublicCollections);
        const installations = lodgeHeritage?.crossReferences?.installations || [];
        const meetingDegreeEvents = lodgeHeritage?.crossReferences?.meetingDegreeEvents || [];
        const degreeRecords = lodgeHeritage?.crossReferences?.degrees || [];
        const categoryLabels = {
            all: "All public heritage",
            installations: "Installations",
            ceremonies: "Convocations and meetings",
            "entered-apprentice": "Entered Apprentice",
            "fellow-craft": "Fellow Craft",
            "master-mason": "Master Mason",
            "mark-master": "Mark Master Mason",
            "royal-arch": "Royal Arch",
            "knight-mason": "Knight Mason",
            templar: "Knights Templar",
            banners: "Banners and standards",
            logos: "Logos, crests, and seals",
            warrants: "Warrants and charters",
            medals: "Medals, jewels, and regalia",
            temples: "Temple and building photos",
            history: "History, consecrations, and anniversaries",
            other: "Other public images",
        };
        const orderDefinitions = [
            {
                key: "craft",
                title: "Irish Craft Masonry",
                subtitle: "Ancient Craft lodges and Craft governance",
                icon: "/images/logo_gli.png",
                terms: ["craft", "lodge", "grand lodge", "provincial grand lodge"],
                sourceLabel: "Grand Lodge of Ireland",
                sourceUrl: "https://freemason.ie/",
                description: [
                    "Candidates enter Freemasonry through the First, or Entered Apprentice, Degree, then progress to the Fellow Craft Degree and finally to the Third, or Sublime Degree of Master Mason.",
                    "The three Craft Degrees are normally completed within about one year, depending on the work of the Lodge. After becoming a Master Mason, further privileges and progression through Lodge offices become possible, culminating in service as Worshipful Master.",
                ],
            },
            {
                key: "royal-arch",
                title: "Royal Arch",
                subtitle: "Royal Arch Chapters and district chapter records",
                icon: "/images/logo_graci.png",
                terms: ["royal arch", "chapter", "rac"],
                sourceLabel: "Supreme Grand Royal Arch Chapter",
                sourceUrl: "https://freemason.ie/about-grand-lodge/appendant-bodies/supreme-grand-royal-arch-chapter-of-ireland/royal-arch-masonry-2/district-racs/",
                description: [
                    "The Royal Arch Degree continues the Temple story described in a Craft Lodge. It is conferred in Royal Arch Chapters, and a Master Mason of one year's good standing is entitled to become a Royal Arch Mason.",
                    "An Irish Freemason's first encounter with a Royal Arch Chapter is in a Lodge of Mark Master Masons, where the Mark Master Mason Degree is conferred. The Mark and Royal Arch Degrees complement a Freemason's knowledge rather than being treated as fourth and fifth Craft Degrees.",
                ],
            },
            {
                key: "knight-masons",
                title: "Council of Knight Masons",
                subtitle: "Knight Mason councils and related public records",
                icon: "/images/logo_gckm.png",
                terms: ["knight mason", "council"],
                sourceLabel: "Grand Council of Knight Masons",
                sourceUrl: "https://freemason.ie/about-grand-lodge/appendant-bodies/",
                description: [
                    "Knight Masonry expands the Temple story further through the building of the second Temple, continuing the narrative found in Craft Lodges and Royal Arch Chapters.",
                    "Three Degrees are conferred in Councils of Knight Masons: Knight of the Sword, Knight of the East, and Knight of the East and West. In the Irish Constitution these Degrees are worked in chronological order, and membership normally requires at least one year in a Royal Arch Chapter.",
                ],
            },
            {
                key: "knights-templar",
                title: "Knights Templar",
                subtitle: "Preceptories and Great Priory heritage",
                icon: "/images/logo_gpi.png",
                terms: ["templar", "preceptory", "great priory"],
                sourceLabel: "Great Priory of Ireland",
                sourceUrl: "https://freemason.ie/about-grand-lodge/appendant-bodies/great-priory-of-ireland/",
                description: [
                    "Membership of the Knights Templar is by invitation only and is limited to those of the Trinitarian Christian faith.",
                    "Members are required to have been a Master Mason for five years and a member of a Royal Arch Chapter for two years before being considered. Requests to join are not entertained.",
                ],
            },
            {
                key: "general",
                title: "General & Other Research",
                subtitle: "Provincial, research, and uncategorised public heritage",
                icon: "/images/research.png",
                terms: ["general", "research", "provincial"],
                sourceLabel: "Connaught 361 Archive",
                sourceUrl: "https://www.connaught361.co.za",
                description: [
                    "Provincial bodies, research records, and public archive material that does not sit neatly inside a Craft lodge, Chapter, Council, or Preceptory.",
                ],
            },
        ];
        const classifyOrder = (lodge) => {
            const text = `${lodge.name || ""} ${lodge.code || ""} ${lodge.kind || ""}`.toLowerCase();
            if (/preceptory|templar|great priory|_kt\b| kt\b/.test(text)) return "knights-templar";
            if (/knight mason|council of knight|shamrock council|gckm/.test(text)) return "knight-masons";
            if (/royal arch|\brac\b|_ra\b|chapter/.test(text)) return "royal-arch";
            if (/provincial|research|board of general purposes|grand lodge|sano|bogp|pgl|general/.test(text)) return "general";
            return "craft";
        };
        const activeOrder = orderDefinitions.some((order) => order.key === lodgeHeritageOrder) ? lodgeHeritageOrder : "all";
        const orderMatches = (lodge) => activeOrder === "all" || classifyOrder(lodge) === activeOrder;
        const orderCounts = orderDefinitions.reduce((acc, order) => {
            acc[order.key] = lodges.filter((lodge) => classifyOrder(lodge) === order.key).length;
            return acc;
        }, {});
        const activeOrderLabel = activeOrder === "all"
            ? "all Irish orders"
            : orderDefinitions.find((order) => order.key === activeOrder)?.title || "selected order";
        const activeOrderDefinition = orderDefinitions.find((order) => order.key === activeOrder);
        const activeOrderSummary = activeOrderDefinition?.description?.[0]
            || "Choose a lodge or body below, or refine the archive with search and date filters.";
        const lodgeSearch = lodgeHeritageSearch.trim().toLowerCase();
        const hasLodgeDateFilter = Boolean(lodgeHeritageDateFrom || lodgeHeritageDateTo);
        const textMatches = (...parts) => !lodgeSearch || normalizeSearchText(parts).includes(lodgeSearch);
        const documentFilterText = (doc = {}) => normalizeSearchText([
            doc.category,
            doc.title,
            doc.description,
            doc.filename,
            doc.documentType,
            doc.eventType,
            formatResearchDate(doc.date),
        ]);
        const filterGroups = [
            {
                title: "Ceremonies",
                options: [
                    { key: "all", label: "All", match: () => true },
                    { key: "installations", label: "Installations", match: (doc) => /installation|installed|installing|investiture|officer/.test(documentFilterText(doc)) },
                    { key: "ceremonies", label: "Convocations & meetings", match: (doc) => doc.category === "ceremonies" || /convocation|regular meeting|meeting \(regular\)|stated communication|communication|chapter meeting|council meeting|ceremony|gathering|function/.test(documentFilterText(doc)) },
                    { key: "entered-apprentice", label: "Entered Apprentice", match: (doc) => /entered apprentice|initiation|initiated/.test(documentFilterText(doc)) },
                    { key: "fellow-craft", label: "Fellow Craft", match: (doc) => /fellow\s*craft|passing|passed/.test(documentFilterText(doc)) },
                    { key: "master-mason", label: "Master Mason", match: (doc) => /master mason|raising|raised/.test(documentFilterText(doc)) && !/mark master|royal arch/.test(documentFilterText(doc)) },
                    { key: "mark-master", label: "Mark Master", match: (doc) => /mark master/.test(documentFilterText(doc)) },
                    { key: "royal-arch", label: "Royal Arch", match: (doc) => /royal arch|exaltation|exalted/.test(documentFilterText(doc)) },
                    { key: "knight-mason", label: "Knight Mason", match: (doc) => /knight mason|knight of the sword|knight of the east|east and west/.test(documentFilterText(doc)) },
                    { key: "templar", label: "Templar", match: (doc) => /templar|preceptory|malta/.test(documentFilterText(doc)) },
                ],
            },
            {
                title: "Objects, Places & History",
                options: [
                    { key: "banners", label: "Banners", match: (doc) => doc.category === "banners" },
                    { key: "warrants", label: "Warrants", match: (doc) => doc.category === "warrants" },
                    { key: "medals", label: "Regalia", match: (doc) => doc.category === "medals" },
                    { key: "temples", label: "Temples", match: (doc) => doc.category === "temples" },
                    { key: "history", label: "Anniversaries & History", match: (doc) => doc.category === "history" || /anniversary|centenary|consecration|dedication|history|stated communication/.test(documentFilterText(doc)) },
                    { key: "other", label: "Other Images", match: (doc) => doc.category === "other" },
                ],
            },
        ];
        const filterOptions = filterGroups.flatMap((group) => group.options);
        const activeFilter = filterOptions.find((option) => option.key === lodgeHeritageCategory) || filterOptions[0];
        const activeCategory = activeFilter.key;
        const activeCategoryLabel = categoryLabels[activeCategory] || activeFilter.label;
        const allOrderDocuments = lodges
            .filter(orderMatches)
            .flatMap((lodge) => (lodge.publicDocuments?.all || []).map((doc) => ({ ...doc, lodgeId: lodge.id, lodgeName: lodge.name })));
        const filterCounts = filterOptions.reduce((acc, option) => {
            acc[option.key] = allOrderDocuments
                .filter((doc) => dateInResearchRange(doc.date, lodgeHeritageDateFrom, lodgeHeritageDateTo))
                .filter(option.match).length;
            return acc;
        }, {});
        const documentInActiveCategory = (doc) => activeFilter.match(doc);
        const documentMatches = (doc) => textMatches(
            doc.title,
            doc.description,
            doc.filename,
            doc.documentType,
            doc.eventType,
            categoryLabels[doc.category] || doc.category,
            activeCategoryLabel,
            formatResearchDate(doc.date)
        );
        const filteredDocuments = (documents = []) => documents
            .filter(documentInActiveCategory)
            .filter((doc) => dateInResearchRange(doc.date, lodgeHeritageDateFrom, lodgeHeritageDateTo))
            .filter((doc) => !lodgeSearch || documentMatches(doc))
            .sort((a, b) => compareResearchDateValues(a.date, b.date, lodgeHeritageSort));
        const matchingPublicDocuments = filteredDocuments(allOrderDocuments);
        const lodgeMatches = (lodge) => {
            const documents = Object.values(lodge.publicDocuments || {}).flat();
            const matchingDocuments = filteredDocuments(documents);
            const hasMatchingCategory = activeCategory === "all" || matchingDocuments.length > 0;
            const searchMatches = textMatches(
                lodge.name,
                lodge.code,
                lodge.kind,
                lodge.status,
                lodge.yearEstablished,
                ...(lodge.eventGroups || []).flatMap((event) => [
                    event.eventType,
                    formatResearchDateRange(event.firstDate, event.lastDate),
                ]),
                ...(lodge.degreeGroups || []).flatMap((degree) => [
                    degree.degree,
                    formatResearchDateRange(degree.firstDate, degree.lastDate),
                ]),
                ...matchingDocuments.flatMap((doc) => [
                    doc.title,
                    doc.description,
                    doc.filename,
                    doc.documentType,
                    doc.eventType,
                    categoryLabels[doc.category] || doc.category,
                ])
            );
            const eventDateMatches = !hasLodgeDateFilter || (lodge.eventGroups || []).some((event) =>
                dateRangeIntersectsResearchRange(event.firstDate, event.lastDate, lodgeHeritageDateFrom, lodgeHeritageDateTo)
            );
            const degreeDateMatches = !hasLodgeDateFilter || (lodge.degreeGroups || []).some((degree) =>
                dateRangeIntersectsResearchRange(degree.firstDate, degree.lastDate, lodgeHeritageDateFrom, lodgeHeritageDateTo)
            );
            const hasMatchingDate = !hasLodgeDateFilter || matchingDocuments.length > 0 || eventDateMatches || degreeDateMatches;
            return orderMatches(lodge) && hasMatchingCategory && hasMatchingDate && searchMatches;
        };
        const filteredLodges = lodges.filter(lodgeMatches);
        const filteredInstallations = installations.filter((item) => textMatches(
            item.lodgeName,
            item.lodgeCode,
            "installation",
            formatResearchDateRange(item.firstDate, item.lastDate)
        ) && dateRangeIntersectsResearchRange(item.firstDate, item.lastDate, lodgeHeritageDateFrom, lodgeHeritageDateTo)
            && (activeCategory === "all" || activeCategory === "installations") && filteredLodges.some((lodge) => String(lodge.id) === String(item.lodgeId)))
            .sort((a, b) => compareResearchDateValues(a.lastDate || a.firstDate, b.lastDate || b.firstDate, lodgeHeritageSort));
        const filteredMeetingDegreeEvents = meetingDegreeEvents.filter((item) => textMatches(
            item.lodgeName,
            item.lodgeCode,
            item.eventType,
            formatResearchDateRange(item.firstDate, item.lastDate)
        ) && dateRangeIntersectsResearchRange(item.firstDate, item.lastDate, lodgeHeritageDateFrom, lodgeHeritageDateTo)
            && ["all", "entered-apprentice", "fellow-craft", "master-mason", "mark-master", "royal-arch", "knight-mason", "templar"].includes(activeCategory)
            && (activeCategory === "all" || activeFilter.match({ title: item.eventType, eventType: item.eventType }))
            && filteredLodges.some((lodge) => String(lodge.id) === String(item.lodgeId)))
            .sort((a, b) => compareResearchDateValues(a.lastDate || a.firstDate, b.lastDate || b.firstDate, lodgeHeritageSort));
        const filteredDegreeRecords = degreeRecords.filter((item) => textMatches(
            item.lodgeName,
            item.lodgeCode,
            item.degree,
            formatResearchDateRange(item.firstDate, item.lastDate)
        ) && dateRangeIntersectsResearchRange(item.firstDate, item.lastDate, lodgeHeritageDateFrom, lodgeHeritageDateTo)
            && ["all", "entered-apprentice", "fellow-craft", "master-mason", "mark-master", "royal-arch", "knight-mason", "templar"].includes(activeCategory)
            && (activeCategory === "all" || activeFilter.match({ title: item.degree, eventType: item.degree }))
            && filteredLodges.some((lodge) => String(lodge.id) === String(item.lodgeId)))
            .sort((a, b) => compareResearchDateValues(a.lastDate || a.firstDate, b.lastDate || b.firstDate, lodgeHeritageSort));
        const previewLimit = lodgeSearch ? 40 : 6;
        const showFullCategoryResults = activeCategory !== "all" || Boolean(lodgeSearch) || hasLodgeDateFilter;
        const limitCategoryResults = (items = [], limit = previewLimit) => (
            showFullCategoryResults ? items : items.slice(0, limit)
        );
        const visibleInstallations = limitCategoryResults(filteredInstallations);
        const visibleMeetingDegreeEvents = limitCategoryResults(filteredMeetingDegreeEvents);
        const visibleDegreeRecords = limitCategoryResults(filteredDegreeRecords, lodgeSearch ? 40 : 8);
        const showInternalRecordIndexes = canManageLodgeHeritage;
        const classifyDegreeFamily = (degree = "") => {
            const text = String(degree).toLowerCase();
            if (/entered apprentice|initiation|first/.test(text)) return "Craft: First Degree";
            if (/fellow\s*craft|passing|second/.test(text)) return "Craft: Second Degree";
            if (/master mason|raising|third|sublime/.test(text)) return "Craft: Third Degree";
            if (/mark master|royal arch/.test(text)) return "Royal Arch and Mark";
            if (/sword|east and west|east\b|knight mason/.test(text)) return "Knight Mason Degrees";
            if (/templar|malta|knight of/.test(text)) return "Knights Templar";
            return "Other side degrees";
        };
        const degreeRecordGroups = Object.values(filteredDegreeRecords.reduce((acc, item) => {
            const family = classifyDegreeFamily(item.degree);
            if (!acc[family]) acc[family] = { family, count: 0, items: [] };
            acc[family].count += Number(item.count || 0);
            acc[family].items.push(item);
            return acc;
        }, {})).sort((a, b) => {
            const order = [
                "Craft: First Degree",
                "Craft: Second Degree",
                "Craft: Third Degree",
                "Royal Arch and Mark",
                "Knight Mason Degrees",
                "Knights Templar",
                "Other side degrees",
            ];
            return order.indexOf(a.family) - order.indexOf(b.family);
        });
        const selectedLodge = lodges.find((lodge) => String(lodge.id) === String(selectedLodgeHeritageId));
        const hasActiveLodgeFilters = activeCategory !== "all" || activeOrder !== "all" || Boolean(lodgeSearch) || hasLodgeDateFilter;
        const resetLodgeExplorer = () => {
            setLodgeHeritageOrder("all");
            setLodgeHeritageCategory("all");
            setLodgeHeritageSearch("");
            setLodgeHeritageDateFrom("");
            setLodgeHeritageDateTo("");
            setSelectedLodgeHeritageId("");
        };
        const scrollLodgeExplorerTo = (id, delay = 40) => {
            setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), delay);
        };
        const openLodgeDetail = (lodgeId) => {
            setSelectedLodgeHeritageId(String(lodgeId || ""));
            scrollLodgeExplorerTo("selected-lodge-heritage", 30);
        };
        const openPublicImagePreview = (doc, collection = "Lodges") => {
            const url = documentDownloadUrl(doc.id, doc.url);
            setPublicImagePreview({
                open: true,
                title: doc.title || "Public archive image",
                content: url,
                downloadUrl: url,
            });
            logResearchAnalytics("research_document_open", {
                collection,
                title: doc.title,
                category: doc.category || activeCategoryLabel,
                documentId: doc.id || "",
            });
        };
        const selectLodgeCategory = (categoryKey) => {
            setLodgeHeritageCategory(categoryKey);
            setSelectedLodgeHeritageId("");
            scrollLodgeExplorerTo(categoryKey === "all" ? "lodge-heritage-bodies" : "lodge-heritage-matches", 40);
        };
        const renderLodgeDocumentGallery = (title, documents = [], limit = 4, lodge = null) => {
            const visibleDocuments = documents.slice(0, limit);
            if (!visibleDocuments.length) return null;

            return (
                <div className="research-lodge-gallery">
                    <h4>{title}</h4>
                    <div>
                        {visibleDocuments.map((doc) => (
                            <article className={`research-lodge-image-card ${doc.published === false ? "draft" : ""}`} key={doc.id || doc.url}>
                                <a
                                    href={documentDownloadUrl(doc.id, doc.url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => logResearchAnalytics("research_document_open", {
                                        collection: "Lodges",
                                        title: doc.title,
                                        category: doc.category || title,
                                        documentId: doc.id || "",
                                    })}
                                >
                                    {doc.isImage ? (
                                        <img
                                            src={doc.thumbnailUrl || documentThumbnailUrl(doc.url)}
                                            alt={doc.title}
                                            loading="lazy"
                                            onError={(event) => applyImageFallback(event, doc.url)}
                                        />
                                    ) : (
                                        <span className="research-lodge-file-tile">{doc.documentType || "Document"}</span>
                                    )}
                                    {canManageLodgeHeritage && (
                                        <small className={doc.published === false ? "research-newsletter-issue-status draft" : "research-newsletter-issue-status"}>
                                            {doc.published === false ? "Draft" : "Published"}
                                        </small>
                                    )}
                                    <span>{doc.title}</span>
                                    {doc.description && <p>{doc.description}</p>}
                                    <em>{formatResearchDate(doc.date) || doc.eventType || "Archive item"}</em>
                                </a>
                                {doc.isImage && (
                                    <button
                                        type="button"
                                        className="research-lodge-expand"
                                        onClick={() => openPublicImagePreview(doc)}
                                    >
                                        Expand Image
                                    </button>
                                )}
                                {canManageLodgeHeritage && (
                                    <button
                                        type="button"
                                        className="research-lodge-publish"
                                        onClick={() => updateLodgeHeritagePublication(doc, doc.published === false)}
                                        disabled={publicCollectionSaving}
                                    >
                                        {doc.published === false ? "Publish Entry" : "Move to Draft"}
                                    </button>
                                )}
                                {canManageLodgeHeritage && doc.isImage && lodge?.id && (
                                    <button
                                        type="button"
                                        className="research-lodge-publish"
                                        onClick={() => updateLodgeHeritageFeaturedImage(lodge, doc)}
                                        disabled={publicCollectionSaving}
                                    >
                                        Use as Archive Image
                                    </button>
                                )}
                                <a
                                    className="research-lodge-download"
                                    href={documentDownloadUrl(doc.id, doc.url)}
                                    download={doc.filename || doc.title || "archive-image"}
                                    onClick={() => logResearchAnalytics("research_document_download", {
                                        collection: "Lodges",
                                        title: doc.title,
                                        category: doc.category || title,
                                        documentId: doc.id || "",
                                    })}
                                >
                                    Download
                                </a>
                            </article>
                        ))}
                    </div>
                </div>
            );
        };

        const renderQuickReferencePanel = ({
            title,
            subtitle,
            count,
            items,
            empty,
            itemKeyPrefix,
            itemLabelPrefix,
            note,
            internal = false,
            grouped = false,
            scrollable = false,
        }) => (
            <article className={internal ? "internal" : ""}>
                <header>
                    <div>
                        <h3>{title}</h3>
                        <p>{subtitle}</p>
                    </div>
                    <strong>{count}</strong>
                </header>
                <div className={`research-lodge-crossref-list ${scrollable ? "scrollable" : ""}`}>
                    {items.length ? items.map((item) => (
                        grouped ? (
                            <div className="research-degree-family" key={item.family}>
                                <h4>{item.family}</h4>
                                <strong>{item.count}</strong>
                                {(scrollable ? item.items : item.items.slice(0, lodgeSearch ? 20 : 4)).map((degree) => (
                                    <button type="button" key={`${degree.lodgeId}-${degree.degree}`} onClick={() => openLodgeDetail(degree.lodgeId)}>
                                        <span>{degree.degree}</span>
                                        <strong>{degree.count}</strong>
                                        <em>Open matching {degree.lodgeName} archive</em>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <button type="button" key={`${itemKeyPrefix}-${item.lodgeId || item.eventType || item.lodgeName}-${item.firstDate || ""}-${item.lastDate || ""}`} onClick={() => openLodgeDetail(item.lodgeId)}>
                                <span>{itemLabelPrefix ? `${itemLabelPrefix}: ${item.lodgeName || item.eventType}` : item.lodgeName || item.eventType}</span>
                                <strong>{item.count}</strong>
                                <em>{item.firstDate || item.lastDate ? `${formatResearchDateRange(item.firstDate, item.lastDate)} · open matching archive` : `Open ${item.lodgeName || "matching records"}`}</em>
                            </button>
                        )
                    )) : <p className="research-muted">{empty}</p>}
                </div>
                {note && <p className="research-lodge-more-note">{note}</p>}
            </article>
        );

        const renderLodgeCard = (lodge) => {
            const publicArchiveItems = (lodge.publicDocuments?.all || []).filter((doc) => doc.published !== false).length;
            const indexedDocuments = Number(lodge.counts?.documents || 0);
            const indexedEvents = Number(lodge.counts?.meetings || 0);
            const indexedDegrees = Number(lodge.counts?.degrees || 0);
            const protectedInventory = indexedDocuments + indexedEvents + indexedDegrees;
            const representativeLabel = lodge.banner?.kind
                ? `${lodge.banner.kind} source${lodge.banner.date ? `: ${formatResearchDate(lodge.banner.date)}` : ""}`
                : "";

            return (
                <article
                    className={`research-lodge-card ${String(selectedLodgeHeritageId) === String(lodge.id) ? "active" : ""} ${lodge.published === false ? "draft" : ""}`}
                    id={`lodge-${lodge.id}`}
                    key={lodge.id}
                >
                    {lodge.banner?.url ? (
                        <img
                            src={lodge.banner.thumbnailUrl || documentThumbnailUrl(lodge.banner.url)}
                            alt={`${lodge.name} banner or standard`}
                            loading="lazy"
                            onError={(event) => applyImageFallback(event, lodge.banner.url, "/images/lodges.png")}
                        />
                    ) : (
                        <div className="research-lodge-banner-placeholder">
                            <img src="/images/lodges.png" alt="" aria-hidden="true" />
                        </div>
                    )}
                    {representativeLabel && <small className="research-lodge-image-source">{representativeLabel}</small>}
                    <div className="research-lodge-card-body">
                        <div className="research-meta-row">
                            <span>{lodge.code}</span>
                            <span>{lodge.kind || "Lodge"}</span>
                            <span>{lodge.status}</span>
                            {lodge.yearEstablished && <span>Est. {lodge.yearEstablished}</span>}
                        </div>
                        {canManageLodgeHeritage && (
                            <small className={lodge.published === false ? "research-newsletter-issue-status draft" : "research-newsletter-issue-status"}>
                                {lodge.published === false ? "Draft" : "Published"}
                            </small>
                        )}
                        <h3>{lodge.name}</h3>
                        <div className="research-lodge-public-count">
                            <strong>{publicArchiveItems.toLocaleString()}</strong>
                            <span>public archive {publicArchiveItems === 1 ? "item" : "items"} available</span>
                        </div>
                        <div className="research-lodge-counts protected">
                            <span><strong>{protectedInventory.toLocaleString()}</strong> protected index total</span>
                            <span><strong>{indexedDocuments.toLocaleString()}</strong> documents</span>
                            <span><strong>{indexedEvents.toLocaleString()}</strong> events</span>
                            <span><strong>{indexedDegrees.toLocaleString()}</strong> degree records</span>
                        </div>
                        <p className="research-lodge-access-note">
                            Public items open here; detailed documents, event notes, and member-level records remain behind login.
                        </p>
                        <div className="research-lodge-card-actions">
                            <button
                                type="button"
                                className="research-lodge-open-label"
                                onClick={() => openLodgeDetail(lodge.id)}
                            >
                                Open public lodge archive
                            </button>
                            {canManageLodgeHeritage && (
                                <button
                                    type="button"
                                    className="research-lodge-card-publish"
                                    onClick={() => updateLodgeHeritageBodyPublication(lodge, lodge.published === false)}
                                    disabled={publicCollectionSaving}
                                >
                                    {lodge.published === false ? "Publish Body" : "Move Body to Draft"}
                                </button>
                            )}
                        </div>
                    </div>
                </article>
            );
        };

        const renderLodgeMatchCard = (doc) => (
            <article className={`research-lodge-match-card ${doc.published === false ? "draft" : ""}`} key={doc.id || doc.url}>
                {doc.isImage ? (
                    <button
                        type="button"
                        className="research-lodge-image-preview-button"
                        onClick={() => openPublicImagePreview(doc)}
                        aria-label={`Expand ${doc.title || "public image"}`}
                    >
                        <img
                            src={doc.thumbnailUrl || documentThumbnailUrl(doc.url)}
                            alt={doc.title}
                            loading="lazy"
                            onError={(event) => applyImageFallback(event, doc.url)}
                        />
                    </button>
                ) : (
                    <a
                        href={documentDownloadUrl(doc.id, doc.url)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => logResearchAnalytics("research_document_open", {
                            collection: "Lodges",
                            title: doc.title,
                            category: doc.category || activeCategoryLabel,
                            documentId: doc.id || "",
                        })}
                    >
                        <span className="research-lodge-file-tile">{doc.documentType || "Document"}</span>
                    </a>
                )}
                <div>
                    {doc.isImage && (
                        <button
                            type="button"
                            className="research-lodge-card-expand"
                            onClick={() => openPublicImagePreview(doc)}
                        >
                            Quick View
                        </button>
                    )}
                    <div className="research-meta-row">
                        <span>{doc.published === false ? "Draft" : "Published"}</span>
                        {doc.date && <span>{formatResearchDate(doc.date)}</span>}
                        {doc.lodgeName && <span>{doc.lodgeName}</span>}
                    </div>
                    <h4>{doc.title}</h4>
                    {doc.description && <p>{doc.description}</p>}
                    <div className="research-lodge-card-actions">
                        <button
                            type="button"
                            className="research-lodge-open-label"
                            onClick={() => openLodgeDetail(doc.lodgeId)}
                        >
                            Open lodge archive
                        </button>
                        <a
                            className="research-lodge-download"
                            href={documentDownloadUrl(doc.id, doc.url)}
                            download={doc.filename || doc.title || "archive-image"}
                            onClick={() => logResearchAnalytics("research_document_download", {
                                collection: "Lodges",
                                title: doc.title,
                                category: doc.category || activeCategoryLabel,
                                documentId: doc.id || "",
                            })}
                        >
                            Download
                        </a>
                        {canManageLodgeHeritage && (
                            <button
                                type="button"
                                className="research-lodge-card-publish"
                                onClick={() => updateLodgeHeritagePublication(doc, doc.published === false)}
                                disabled={publicCollectionSaving}
                            >
                                {doc.published === false ? "Publish Entry" : "Move to Draft"}
                            </button>
                        )}
                        {canManageLodgeHeritage && doc.isImage && (
                            <button
                                type="button"
                                className="research-lodge-card-publish"
                                onClick={() => {
                                    const lodge = lodges.find((candidate) => String(candidate.id) === String(doc.lodgeId));
                                    updateLodgeHeritageFeaturedImage(lodge, doc);
                                }}
                                disabled={publicCollectionSaving}
                            >
                                Use as Archive Image
                            </button>
                        )}
                    </div>
                </div>
            </article>
        );

        const renderSelectedLodgeDetail = (lodge) => {
            if (!lodge) return null;

            const gallerySections = [
                ["Installation photos", lodge.publicDocuments?.installations],
                ["Convocations, meetings, and ceremony photos", lodge.publicDocuments?.ceremonies],
                ["Banners and standards", lodge.publicDocuments?.banners],
                ["Logos, crests, and seals", lodge.publicDocuments?.logos],
                ["Medals, jewels, and regalia", lodge.publicDocuments?.medals],
                ["Warrants and charters", lodge.publicDocuments?.warrants],
                [
                    ["entered-apprentice", "fellow-craft", "master-mason", "mark-master", "royal-arch", "knight-mason", "templar"].includes(activeCategory)
                        ? `${activeCategoryLabel} photos`
                        : "Degree ceremony photos",
                    lodge.publicDocuments?.degrees,
                ],
                ["History and anniversary photos", lodge.publicDocuments?.history],
                ["Temple and building photos", lodge.publicDocuments?.temples],
                ["Other public images", lodge.publicDocuments?.other],
            ].map(([title, documents]) => [title, filteredDocuments(documents || [], 12)])
                .filter(([, documents]) => documents.length);

            return (
                <section className="research-lodge-selected" id="selected-lodge-heritage" aria-label={`${lodge.name} archive`}>
                    <div className="research-lodge-selected-header">
                        <div className="research-lodge-selected-image">
                            {lodge.banner?.url ? (
                                <img
                                    src={lodge.banner.thumbnailUrl || documentThumbnailUrl(lodge.banner.url)}
                                    alt={`${lodge.name} representative archive source`}
                                    loading="lazy"
                                    onError={(event) => applyImageFallback(event, lodge.banner.url, "/images/lodges.png")}
                                />
                            ) : (
                                <div className="research-lodge-banner-placeholder">
                                    <img src="/images/lodges.png" alt="" aria-hidden="true" />
                                </div>
                            )}
                            {lodge.banner?.kind && (
                                <small className="research-lodge-image-source">
                                    {lodge.banner.kind} source{lodge.banner.date ? `: ${formatResearchDate(lodge.banner.date)}` : ""}
                                </small>
                            )}
                        </div>
                        <div>
                            <div className="research-meta-row">
                                <span>{lodge.code}</span>
                                <span>{lodge.kind || "Lodge"}</span>
                                <span>{lodge.status}</span>
                                {lodge.yearEstablished && <span>Est. {lodge.yearEstablished}</span>}
                            </div>
                            {canManageLodgeHeritage && (
                                <small className={lodge.published === false ? "research-newsletter-issue-status draft" : "research-newsletter-issue-status"}>
                                    {lodge.published === false ? "Draft" : "Published"}
                                </small>
                            )}
                            <h3>{lodge.name}</h3>
                            <p>
                                Showing {activeCategoryLabel.toLowerCase()} for this lodge. Minutes, agendas, summonses, invitations, and member-level records remain protected.
                            </p>
                            <div className="research-lodge-selected-counts">
                                <span><strong>{(lodge.publicDocuments?.all || []).filter((doc) => doc.published !== false).length.toLocaleString()}</strong> public archive items</span>
                                <span><strong>{Number(lodge.counts?.documents || 0).toLocaleString()}</strong> indexed documents behind login</span>
                                <span><strong>{Number(lodge.counts?.meetings || 0).toLocaleString()}</strong> indexed events behind login</span>
                                <span><strong>{Number(lodge.counts?.degrees || 0).toLocaleString()}</strong> degree records behind login</span>
                            </div>
                        </div>
                        {canManageLodgeHeritage && (
                            <button
                                type="button"
                                className="research-lodge-card-publish"
                                onClick={() => updateLodgeHeritageBodyPublication(lodge, lodge.published === false)}
                                disabled={publicCollectionSaving}
                            >
                                {lodge.published === false ? "Publish Body" : "Move Body to Draft"}
                            </button>
                        )}
                        <button type="button" onClick={() => setSelectedLodgeHeritageId("")}>Close</button>
                    </div>
                    {canManageLodgeHeritage && (
                        <div className="research-lodge-selected-grid internal">
                            <section>
                                <h4>Internal event summary</h4>
                                {lodge.eventGroups?.length ? lodge.eventGroups.slice(0, 8).map((event) => (
                                    <p key={event.eventType}>
                                        <strong>{event.count}</strong>
                                        <span>{event.eventType}</span>
                                        <em>{formatResearchDateRange(event.firstDate, event.lastDate)}</em>
                                    </p>
                                )) : <p className="research-muted">No event summary is available for this lodge yet.</p>}
                            </section>
                            <section>
                                <h4>Internal degree summary</h4>
                                {lodge.degreeGroups?.length ? lodge.degreeGroups.slice(0, 8).map((degree) => (
                                    <p key={degree.degree}>
                                        <strong>{degree.count}</strong>
                                        <span>{degree.degree}</span>
                                        <em>{formatResearchDateRange(degree.firstDate, degree.lastDate)}</em>
                                    </p>
                                )) : <p className="research-muted">No aggregate degree summary is available for this lodge yet.</p>}
                            </section>
                        </div>
                    )}
                    {gallerySections.length ? gallerySections.map(([title, documents]) => (
                        <div key={title}>
                            {renderLodgeDocumentGallery(title, documents, 12, lodge)}
                        </div>
                    )) : (
                        <p className="research-empty">No public images match this lodge, category, and search.</p>
                    )}
                </section>
            );
        };

        return (
            <div className="research-lodge-heritage">
                {lodgeHeritageLoading && <p className="research-muted">Loading public lodge heritage...</p>}
                {lodgeHeritageError && <p className="research-empty error">{lodgeHeritageError}</p>}
                {!lodgeHeritageLoading && !lodgeHeritageError && lodgeHeritage && (
                    <>
                        <section className="research-lodge-summary" aria-label="Lodge heritage summary">
                            <div>
                                <strong>{lodgeHeritage.totals?.lodges || 0}</strong>
                                <span>lodges with public index material</span>
                            </div>
                            <div>
                                <strong>{lodgeHeritage.totals?.banners || 0}</strong>
                                <span>banner or standard images</span>
                            </div>
                            <div>
                                <strong>{lodgeHeritage.totals?.installationPhotos || 0}</strong>
                                <span>installation photos</span>
                            </div>
                            <div>
                                <strong>{lodgeHeritage.totals?.ceremonyPhotos || 0}</strong>
                                <span>convocation and meeting photos</span>
                            </div>
                            <div>
                                <strong>{lodgeHeritage.totals?.medals || 0}</strong>
                                <span>medals and regalia</span>
                            </div>
                            {canManageLodgeHeritage && (
                                <>
                                    <div>
                                        <strong>{lodgeHeritage.totals?.meetings || 0}</strong>
                                        <span>internal event records</span>
                                    </div>
                                    <div>
                                        <strong>{lodgeHeritage.totals?.degrees || 0}</strong>
                                        <span>internal degree records</span>
                                    </div>
                                </>
                            )}
                        </section>

                        <section className="research-order-gateway" aria-label="Irish order gateway">
                            {orderDefinitions.map((order) => (
                                <button
                                    type="button"
                                    key={order.key}
                                    className={activeOrder === order.key ? "active" : ""}
                                    onClick={() => {
                                        setLodgeHeritageOrder(order.key);
                                        setLodgeHeritageCategory("all");
                                        setSelectedLodgeHeritageId("");
                                        scrollLodgeExplorerTo("lodge-heritage-bodies", 60);
                                    }}
                                    aria-pressed={activeOrder === order.key}
                                >
                                    <img src={order.icon} alt="" aria-hidden="true" />
                                    <strong>{order.title}</strong>
                                    <a
                                        href={order.sourceUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        Source: {order.sourceLabel}
                                    </a>
                                    <span>{order.subtitle}</span>
                                    <em>{orderCounts[order.key] || 0} indexed bodies</em>
                                </button>
                            ))}
                        </section>

                        {activeOrderDefinition && (
                            <section className="research-order-footnote" aria-label={`${activeOrderDefinition.title} sources`}>
                                <p>
                                    Sources: “An Initial Guide to Freemasonry in Ireland”, Grand Lodge of Ireland;{" "}
                                    <a href={activeOrderDefinition.sourceUrl} target="_blank" rel="noreferrer">
                                        {activeOrderDefinition.sourceLabel}
                                    </a>
                                    ;{" "}
                                    <a href="https://irishfreemasonry.co.za" target="_blank" rel="noreferrer">
                                        Irish Freemasonry South Africa
                                    </a>
                                    .
                                </p>
                            </section>
                        )}

                        <section className="research-lodge-search" id="lodge-heritage-controls" aria-label="Search lodge heritage">
                            <label htmlFor="lodge-heritage-search">
                                Cross-reference search
                                <input
                                    id="lodge-heritage-search"
                                    type="search"
                                    value={lodgeHeritageSearch}
                                    onChange={(event) => setLodgeHeritageSearch(event.target.value)}
                                    placeholder="Search person, lodge, degree, installation, medal, warrant, date..."
                                />
                            </label>
                            {lodgeSearch && (
                                <button type="button" onClick={() => setLodgeHeritageSearch("")}>
                                    Clear
                                </button>
                            )}
                            {renderResearchDateFilters({
                                from: lodgeHeritageDateFrom,
                                to: lodgeHeritageDateTo,
                                sort: lodgeHeritageSort,
                                onFromChange: setLodgeHeritageDateFrom,
                                onToChange: setLodgeHeritageDateTo,
                                onSortChange: setLodgeHeritageSort,
                                onClear: () => {
                                    setLodgeHeritageDateFrom("");
                                    setLodgeHeritageDateTo("");
                                },
                                copy: "Filter public lodge images, ceremony photos, warrants, banners, and the direct lodge/body index by date.",
                            })}
                            <p>
                                {lodgeSearch
                                    ? `Showing ${filteredLodges.length.toLocaleString()} direct lodge/body card${filteredLodges.length === 1 ? "" : "s"} and ${matchingPublicDocuments.length.toLocaleString()} public archive record${matchingPublicDocuments.length === 1 ? "" : "s"} for "${lodgeHeritageSearch.trim()}".`
                                    : `Browsing ${activeOrderLabel}, filtered to ${activeCategoryLabel.toLowerCase()}: ${filteredLodges.length.toLocaleString()} direct lodge/body card${filteredLodges.length === 1 ? "" : "s"} and ${matchingPublicDocuments.length.toLocaleString()} public archive record${matchingPublicDocuments.length === 1 ? "" : "s"} match the current filters.`}
                                {canManageLodgeHeritage ? " Logged-in users can also review protected record indexes for curation." : " Public results only include published archive images and documents."}
                            </p>
                        </section>

                        <section className="research-lodge-filter-panel" id="lodge-heritage-category-tags" aria-label="Public archive filters">
                            {filterGroups.map((group) => (
                                <div key={group.title}>
                                    <h3>{group.title}</h3>
                                    <div className="research-lodge-category-strip">
                                        {group.options.map((option) => {
                                            const count = filterCounts[option.key] || 0;
                                            return (
                                                <button
                                                    type="button"
                                                    key={option.key}
                                                    className={activeCategory === option.key ? "active" : ""}
                                                    onClick={() => selectLodgeCategory(option.key)}
                                                    disabled={option.key !== "all" && !count}
                                                    aria-pressed={activeCategory === option.key}
                                                >
                                                    <strong>{count}</strong>
                                                    <span>{option.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            <div className="research-lodge-current-view">
                                <div>
                                    <strong>Current view</strong>
                                    <span>
                                        {activeOrderLabel} · {activeCategoryLabel.toLowerCase()}
                                        {hasLodgeDateFilter ? " · date filtered" : ""}
                                        {lodgeSearch ? ` · search: ${lodgeHeritageSearch.trim()}` : ""}
                                    </span>
                                </div>
                                <p>Use search, dates, and category chips to narrow the direct lodge/body list and public records below. Choosing an order opens the matching body list directly.</p>
                                {hasActiveLodgeFilters && (
                                    <button type="button" onClick={resetLodgeExplorer}>Reset explorer</button>
                                )}
                            </div>
                        </section>

                        {(activeCategory !== "all" || lodgeSearch || hasLodgeDateFilter) && (
                            <section className={`research-lodge-matches ${showFullCategoryResults ? "filtered" : ""}`} id="lodge-heritage-matches" aria-label="Matching public archive material">
                                <div className="research-lodge-matches-header">
                                    <div>
                                        <p className="research-eyebrow">Matching public material</p>
                                        <h3>{activeCategoryLabel}</h3>
                                    </div>
                                    <div className="research-lodge-matches-tools">
                                        <span>{matchingPublicDocuments.length} record{matchingPublicDocuments.length === 1 ? "" : "s"}</span>
                                        <label>
                                            <span>Sort</span>
                                            <select value={lodgeHeritageSort} onChange={(event) => setLodgeHeritageSort(event.target.value)}>
                                                <option value="latest">Latest first</option>
                                                <option value="oldest">Oldest first</option>
                                            </select>
                                        </label>
                                    </div>
                                </div>
                                {matchingPublicDocuments.length ? (
                                    <div className="research-lodge-match-scroll" tabIndex="0">
                                        <div className="research-lodge-match-grid">
                                            {matchingPublicDocuments.map(renderLodgeMatchCard)}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="research-muted">No public archive documents match this combination of tag, search, and date range.</p>
                                )}
                                {matchingPublicDocuments.length > 12 && (
                                    <p className="research-lodge-more-note">All matching public records are shown in the scrollable tray above. Use search, dates, or sort order to refine the list.</p>
                                )}
                            </section>
                        )}

                        <section className="research-lodge-browser priority" id="lodge-heritage-bodies" aria-label="Browse lodge and order bodies">
                            <div className="research-lodge-browser-header">
                                <div>
                                    <p className="research-eyebrow">Open a lodge or body</p>
                                    <h3>{activeOrder === "all" ? "All lodge and order bodies" : `${activeOrderDefinition?.title || activeOrderLabel} bodies`}</h3>
                                    <p>
                                        {activeOrder === "all"
                                            ? "Choose an order above, or open any public lodge/body directly. These cards are the main route into each archive."
                                            : activeOrderSummary}
                                    </p>
                                </div>
                                <div className="research-lodge-browser-actions">
                                    <span>{filteredLodges.length} indexed bod{filteredLodges.length === 1 ? "y" : "ies"}</span>
                                    {activeOrder !== "all" && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLodgeHeritageOrder("all");
                                                setSelectedLodgeHeritageId("");
                                                scrollLodgeExplorerTo("lodge-heritage-bodies", 30);
                                            }}
                                        >
                                            Show all
                                        </button>
                                    )}
                                </div>
                            </div>
                            {filteredLodges.length ? (
                                <div className="research-lodge-grid direct">
                                    {filteredLodges.map((lodge) => renderLodgeCard(lodge))}
                                </div>
                            ) : (
                                <p className="research-muted">No public lodge/body cards match this order and search.</p>
                            )}
                        </section>

                        {selectedLodge && renderSelectedLodgeDetail(selectedLodge)}

                        {showInternalRecordIndexes && (
                            <details className="research-lodge-tools-panel">
                                <summary>
                                    <span>Archivist-only protected summaries</span>
                                    <em>{(filteredInstallations.length + filteredMeetingDegreeEvents.length + filteredDegreeRecords.length).toLocaleString()} internal row{(filteredInstallations.length + filteredMeetingDegreeEvents.length + filteredDegreeRecords.length) === 1 ? "" : "s"}</em>
                                </summary>
                                <section className="research-lodge-crossrefs internal" aria-label="Internal lodge record indexes">
                                    <div className="research-lodge-crossrefs-header">
                                        <p className="research-eyebrow">Archivist-only index</p>
                                        <h3>Protected summaries for curation</h3>
                                        <span>{(filteredInstallations.length + filteredMeetingDegreeEvents.length + filteredDegreeRecords.length).toLocaleString()} internal index row{(filteredInstallations.length + filteredMeetingDegreeEvents.length + filteredDegreeRecords.length) === 1 ? "" : "s"}</span>
                                    </div>
                                    {renderQuickReferencePanel({
                                        title: "Protected event summaries",
                                        subtitle: "Meeting and ceremony counts only. Source records stay behind login.",
                                        count: filteredInstallations.length + filteredMeetingDegreeEvents.length,
                                        items: [
                                            ...visibleInstallations.map((item) => ({ ...item, lodgeName: item.lodgeName, firstDate: item.firstDate, lastDate: item.lastDate })),
                                            ...visibleMeetingDegreeEvents.map((item) => ({ ...item, lodgeName: item.eventType, firstDate: item.firstDate, lastDate: item.lastDate, eventType: item.lodgeName })),
                                        ],
                                        empty: "No formal event records match this search.",
                                        itemKeyPrefix: "protected-event",
                                        scrollable: showFullCategoryResults,
                                        note: !showFullCategoryResults && (filteredInstallations.length + filteredMeetingDegreeEvents.length) > (visibleInstallations.length + visibleMeetingDegreeEvents.length)
                                            ? "Search to narrow the full event index."
                                            : "Visible only when logged in. These records are not public archive entries.",
                                        internal: true,
                                    })}
                                    {renderQuickReferencePanel({
                                        title: "Protected degree summaries",
                                        subtitle: "Aggregate degree counts grouped by pathway.",
                                        count: filteredDegreeRecords.reduce((sum, item) => sum + Number(item.count || 0), 0),
                                        items: degreeRecordGroups,
                                        empty: "No aggregate degree records are available yet.",
                                        itemKeyPrefix: "protected-degree",
                                        note: !showFullCategoryResults && filteredDegreeRecords.length > visibleDegreeRecords.length
                                            ? "Showing grouped examples. Search for a specific degree, lodge, or person to narrow this index."
                                            : "",
                                        internal: true,
                                        grouped: true,
                                        scrollable: showFullCategoryResults,
                                    })}
                                </section>
                            </details>
                        )}
                    </>
                )}
            </div>
        );
    };

    const renderSurveyResults = () => (
        <div className="research-survey-report">
            {surveyLoading && <div className="research-empty">Loading survey analysis...</div>}
            {surveyError && <div className="research-empty error">{surveyError}</div>}
            {!surveyLoading && !surveyError && surveyResults && (
                <>{(() => {
                    const recommendationQuestion = getSurveyQuestion(surveyResults, "recommend_to_friend");
                    const activityQuestion = getSurveyQuestion(surveyResults, "current_activity");
                    const connectionQuestion = getSurveyQuestion(surveyResults, "connected_to_lodge");
                    const barrierQuestion = getSurveyQuestion(surveyResults, "participation_barriers");
                    const motivationQuestion = getSurveyQuestion(surveyResults, "motivation_joining");
                    const experienceMatrixQuestion = getSurveyQuestion(surveyResults, "experience_ratings");
                    const hasChartData = Boolean(
                        recommendationQuestion?.total ||
                        activityQuestion?.total ||
                        connectionQuestion?.total ||
                        barrierQuestion?.total ||
                        motivationQuestion?.total ||
                        experienceMatrixQuestion?.total
                    );

                    return (
                        <>
                    <div className="research-survey-status">
                        <span>{surveyResults.is_active ? "Open" : "Closed"}</span>
                        <strong>{surveyResults.responseCount}</strong>
                        <span>anonymous responses</span>
                    </div>

                    <section className="research-survey-summary" aria-label="Survey summary">
                        <h3>{surveyResults.title}</h3>
                        {surveyResults.analysis?.closingStatement && (
                            <p className="research-survey-closing">{surveyResults.analysis.closingStatement}</p>
                        )}
                        {(surveyResults.analysis?.summary || []).map((line) => (
                            <p key={line}>{line}</p>
                        ))}
                        {surveyResults.analysis?.methodNote && (
                            <p className="research-muted">{surveyResults.analysis.methodNote}</p>
                        )}
                    </section>

                    {surveyResults.analysis?.keyMetrics?.length > 0 && (
                        <section className="research-survey-metrics" aria-label="Survey key metrics">
                            {surveyResults.analysis.keyMetrics.map((metric) => (
                                <div key={metric.label}>
                                    <strong>{metric.value}</strong>
                                    <span>{metric.label}</span>
                                    <em>{metric.detail}</em>
                                </div>
                            ))}
                        </section>
                    )}

                    {surveyResults.analysis?.reportSections?.length > 0 && (
                        <section className="research-survey-narrative" aria-label="Survey narrative analysis">
                            {surveyResults.analysis.reportSections.map((section) => (
                                <article key={section.title}>
                                    <h4>{section.title}</h4>
                                    <p>{section.body}</p>
                                    {section.evidence?.length > 0 && (
                                        <div className="research-survey-evidence">
                                            {section.evidence.map((row) => (
                                                <span key={`${section.title}-${row.label}`}>
                                                    {row.label}: <strong>{row.count}</strong>
                                                    {row.percent !== null && row.percent !== undefined ? ` (${row.percent}%)` : ""}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </article>
                            ))}
                        </section>
                    )}

                    <section className="research-survey-findings" aria-label="Survey findings">
                        {(surveyResults.analysis?.findings || []).map((finding) => (
                            <article key={finding.title}>
                                <h4>{finding.title}</h4>
                                <p>{finding.body}</p>
                                {finding.evidence?.length > 0 && (
                                    <div className="research-survey-evidence">
                                        {finding.evidence.map((row) => (
                                            <span key={`${finding.title}-${row.label}`}>
                                                {row.label}: <strong>{row.count}</strong>
                                                {row.percent !== null && row.percent !== undefined ? ` (${row.percent}%)` : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </article>
                        ))}
                    </section>

                    {hasChartData && (
                        <section className="research-survey-charts" aria-label="Survey charts">
                            <div className="research-survey-chart-card">
                                <ReactECharts option={surveyDonutOption(recommendationQuestion, "Recommendation / Join Again")} style={{ height: 320 }} />
                            </div>
                            <div className="research-survey-chart-card">
                                <ReactECharts option={surveyBarOption(connectionQuestion, "Connection to Lodge")} style={{ height: 320 }} />
                            </div>
                            <div className="research-survey-chart-card">
                                <ReactECharts option={surveyBarOption(activityQuestion, "Current Engagement")} style={{ height: 320 }} />
                            </div>
                            <div className="research-survey-chart-card wide">
                                <ReactECharts option={surveyMatrixOption(experienceMatrixQuestion)} style={{ height: 360 }} />
                            </div>
                            <div className="research-survey-chart-card">
                                <ReactECharts option={surveyBarOption(motivationQuestion, "Why Brethren Joined")} style={{ height: 360 }} />
                            </div>
                            <div className="research-survey-chart-card">
                                <ReactECharts option={surveyBarOption(barrierQuestion, "Participation Barriers")} style={{ height: 360 }} />
                            </div>
                        </section>
                    )}

                    {surveyResults.analysis?.recommendations?.length > 0 && (
                        <section className="research-survey-recommendations" aria-label="Survey recommendations">
                            <div>
                                <p className="research-eyebrow">Closing Recommendations</p>
                                <h3>What the survey suggests we should do next</h3>
                            </div>
                            <ol>
                                {surveyResults.analysis.recommendations.map((recommendation) => (
                                    <li key={recommendation}>{recommendation}</li>
                                ))}
                            </ol>
                        </section>
                    )}

                    <section className="research-survey-appendix" aria-label="Survey appendix">
                        <div>
                            <p className="research-eyebrow">Appendix</p>
                            <h3>Questions and Aggregate Results</h3>
                            <p>{surveyResults.analysis?.appendixNote}</p>
                        </div>
                        {(surveyResults.questions || []).map((question) => (
                            <details key={question.id}>
                                <summary>
                                    <span>{question.section}</span>
                                    <strong>{question.label}</strong>
                                    <em>{question.total} response{question.total === 1 ? "" : "s"}</em>
                                </summary>
                                {renderSurveyQuestionAppendix(question)}
                            </details>
                        ))}
                    </section>
                        </>
                    );
                })()}</>
            )}
        </div>
    );

    return (
        <main className="research-page">
            <section className="research-hero">
                <div>
                    <p className="research-eyebrow">Connaught Research Lodge Archive</p>
                    <h1>Research & Heritage</h1>
                    <p>
                        A public research area for newsletters, lodge heritage, charity work, surveys, research papers, and cultural archive material. Public records open the source trail; deeper archival records remain protected for verified users.
                    </p>
                    <p className="research-citation-notice">
                        If you quote, reuse, or publish material from this archive, please cite the Connaught Research Lodge Archive and the listed source where one is provided.
                    </p>
                </div>
            </section>

            <section className="research-library" aria-labelledby="research-library-title">
                <div className="research-section-header">
                    <div>
                        <p className="research-eyebrow">Public archive</p>
                        <h2 id="research-library-title">Public & Research Hub</h2>
                    </div>
                    <div className="research-section-actions">
                        {renderPublicationQueueButton()}
                        <span>Public entries and member tools</span>
                    </div>
                </div>

                <div className="research-collection-grid">
                    {researchGroups.map((group) => {
                        const content = (
                            <>
                                {group.icon && <img src={group.icon} alt="" className="research-collection-icon" aria-hidden="true" />}
                                <div className="research-collection-header">
                                    <p className="research-eyebrow">{group.eyebrow}</p>
                                    <h3>{group.title}</h3>
                                    <p>{group.description}</p>
                                </div>
                                <div className="research-collection-footer">
                                    <span>{group.entries.length} collection{group.entries.length === 1 ? "" : "s"}</span>
                                    <span className="research-card-action" aria-hidden="true">
                                        {group.cta}
                                    </span>
                                </div>
                            </>
                        );

                        if (group.href) {
                            return (
                                <article
                                    className="research-collection-card interactive"
                                    key={group.title}
                                    role="link"
                                    tabIndex={0}
                                    onClick={() => navigate(group.href)}
                                    onKeyDown={(event) => handleCardKeyDown(event, () => navigate(group.href))}
                                    aria-label={`Open ${group.title}`}
                                >
                                    {content}
                                </article>
                            );
                        }

                        return (
                            <article
                                className="research-collection-card interactive"
                                key={group.title}
                                role="button"
                                tabIndex={0}
                                onClick={() => openResearchGroup(group)}
                                onKeyDown={(event) => handleCardKeyDown(event, () => openResearchGroup(group))}
                                aria-label={`Open ${group.title}`}
                            >
                                {content}
                            </article>
                        );
                    })}
                </div>
            </section>

            {selectedCollection && (
                <div className="research-detail-backdrop" role="dialog" aria-modal="true" aria-labelledby="research-collection-title">
                    <div className="research-detail-modal compact collection">
                        <button
                            type="button"
                            className="research-detail-close"
                            onClick={() => setSelectedCollection(null)}
                            aria-label="Close collection"
                        >
                            &times;
                        </button>
                        <div className="research-detail-content">
                            <div className="research-collection-page-header">
                                {selectedCollection.icon && <img src={selectedCollection.icon} alt="" aria-hidden="true" />}
                                <div>
	                                    <p className="research-eyebrow">{selectedCollection.eyebrow}</p>
	                                    <h2 id="research-collection-title">{selectedCollection.title}</h2>
	                                    <p>{selectedCollection.description}</p>
                                        {renderPublicationQueueButton("subtle")}
	                                    {renderArchiveUpdateNotice()}
	                                </div>
	                            </div>
                            <div className="research-topic-grid collection-items">
                                {selectedCollection.entries.map((entry) => {
                                    const isLockedEntry = entry.contentType === "research-papers" && !user;

                                    return (
                                        <article
                                            className={`research-topic-card interactive ${entry.contentType === "looking-east" ? "newsletter-topic-card" : ""} ${entry.contentType === "survey-results" ? "survey-topic-card" : ""} ${isLockedEntry ? "locked-topic-card" : ""}`}
                                            key={entry.title}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openCollectionEntry(entry)}
                                            onKeyDown={(event) => handleCardKeyDown(event, () => openCollectionEntry(entry))}
                                            aria-label={`Open ${entry.title}`}
                                        >
                                            {entry.icon && <img src={entry.icon} alt="" className="research-topic-icon" aria-hidden="true" />}
                                            <div className="research-meta-row">
                                                <span>{entry.type}</span>
                                                <span>{entry.status}</span>
                                            </div>
                                            <h3>{entry.title}</h3>
                                            <p>{entry.subtitle}</p>
                                            {entry.contentType === "looking-east" && (
                                                <div className="newsletter-feature-row" aria-hidden="true">
                                                    <span>Search issues</span>
                                                    <span>Browse contents</span>
                                                    <span>Open pages</span>
                                                </div>
                                            )}
                                            <button type="button" className="research-card-action">
                                                {entry.cta || "Open Topic"}
                                            </button>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedTopic && (
                <div className="research-detail-backdrop" role="dialog" aria-modal="true" aria-labelledby="research-topic-title">
                    <div className={`research-detail-modal compact ${selectedTopic.contentType === "looking-east" ? "newsletter-modal" : ""}`}>
                        <button
                            type="button"
                            className="research-detail-close"
                            onClick={() => setSelectedTopic(null)}
                            aria-label="Close topic detail"
                        >
                            &times;
                        </button>
                        <div className="research-detail-content">
                            <div className="research-meta-row">
                                <span>{selectedTopic.type}</span>
                                <span>{selectedTopic.status}</span>
                            </div>
                            {renderPublicationQueueButton("subtle")}
                            {selectedTopic.contentType === "looking-east" ? (
                                <div className="research-newsletter-modal-header">
                                    <h2 id="research-topic-title" className="research-sr-only">Looking East</h2>
                                    {lookingEastMasthead("modal")}
                                    <p>Search articles and issues, then open the original PDF at the relevant page.</p>
                                    <details className="newsletter-index-about">
                                        <summary>About this public index and citing sources</summary>
                                        <p>{selectedTopic.detail}</p>
                                        {renderArchiveUpdateNotice()}
                                    </details>
                                </div>
                            ) : (
	                                <>
	                                    <h2 id="research-topic-title">{selectedTopic.title}</h2>
	                                    <p>{selectedTopic.detail}</p>
	                                </>
	                            )}
	                            {selectedTopic.contentType !== "looking-east" && renderArchiveUpdateNotice()}
	                            <div className="research-topic-next">
                                {selectedTopic.contentType === "survey-results" && renderSurveyResults()}
                                {selectedTopic.contentType === "archive-topic" && renderArchiveTopic(selectedTopic)}
                                {selectedTopic.contentType === "charity-case" && renderCharityCase(selectedTopic)}
                                {selectedTopic.contentType === "historical-highlights" && renderHistoricalHighlights()}
                                {selectedTopic.contentType === "charity-fundraising" && renderCharityFundraising(selectedTopic)}
                                {selectedTopic.contentType === "lodge-heritage" && renderLodgeHeritage(selectedTopic)}
                                {selectedTopic.contentType === "collection-plan" && renderCollectionPlan(selectedTopic)}
                                {selectedTopic.contentType === "public-document-collection" && renderPublicDocumentCollection(selectedTopic)}
                                {selectedTopic.contentType === "looking-east" && (
                                    <div className="research-newsletter-workspace">
                                            <div className="research-newsletter-summary compact">
                                                <div>
                                                    <p className="research-eyebrow">Looking East archive</p>
                                                    <h3>Quarterly newsletter index</h3>
                                                <p>
                                                    Choose an issue to browse its contents, or search across the collection.
                                                </p>
                                                {canManageNewsletter && (
                                                    <div className="research-newsletter-admin-actions">
                                                        <button
                                                            type="button"
                                                            className="research-refresh-button"
                                                            onClick={() => fetchLookingEastIndex(true)}
                                                            disabled={newsletterRefreshing}
                                                        >
                                                            {newsletterRefreshing ? "Refreshing..." : "Refresh Index"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="research-refresh-button secondary"
                                                            onClick={() => openNewsletterEditor("add")}
                                                            disabled={!selectedNewsletterIssue}
                                                        >
                                                            Add Contents Entry
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="research-refresh-button secondary"
                                                            onClick={() => openNewsletterEditor("add", selectedNewsletterIssue, null, { category: "Advert" })}
                                                            disabled={!selectedNewsletterIssue}
                                                        >
                                                            Add Advert
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="research-newsletter-stats">
                                                <span>{newsletterStats.yearRange}</span>
                                                <strong>{newsletterLoading ? "..." : newsletterIssues.length}</strong>
                                                <span>issues indexed</span>
                                            </div>
                                            <div className="research-newsletter-stats">
                                                <span>Extracted</span>
                                                <strong>{newsletterLoading ? "..." : newsletterStats.totalPages}</strong>
                                                <span>pages</span>
                                            </div>
                                        </div>

                                        <div className="research-newsletter-search-row">
                                            <label className="research-newsletter-search">
                                                <span>General search</span>
                                                <input
                                                    type="search"
                                                    value={newsletterSearch}
                                                    onChange={(event) => setNewsletterSearch(event.target.value)}
                                                    placeholder="Search Royal Arch, bylaws, charity, events..."
                                                />
                                            </label>
                                            <label className="research-newsletter-search category">
                                                <span>Category</span>
                                                <select
                                                    value={newsletterCategory}
                                                    onChange={(event) => setNewsletterCategory(event.target.value)}
                                                >
                                                    <option value="">All categories</option>
                                                    {newsletterCategories.map((category) => (
                                                        <option key={category} value={category}>{category}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            {renderResearchDateFilters({
                                                from: newsletterDateFrom,
                                                to: newsletterDateTo,
                                                sort: newsletterSort,
                                                onFromChange: setNewsletterDateFrom,
                                                onToChange: setNewsletterDateTo,
                                                onSortChange: setNewsletterSort,
                                                onClear: () => {
                                                    setNewsletterDateFrom("");
                                                    setNewsletterDateTo("");
                                                },
                                                copy: "Filter newsletter issues and contents entries by issue date.",
                                            })}
                                        </div>

                                        {newsletterLoading && <div className="research-empty">Indexing newsletter contents...</div>}
                                        {newsletterError && <div className="research-empty error">{newsletterError}</div>}

                                        {!newsletterLoading && !newsletterError && (
                                            <>
                                                <div className="research-newsletter-issue-grid" aria-label="Looking East issues">
                                                    {visibleNewsletterIssues.length === 0 ? (
                                                        <div className="research-empty">
                                                            {canManageNewsletter
                                                                ? `No Looking East issues are currently visible in this index. Source documents found: ${newsletterIndexMeta?.sourceDocuments ?? 0}; server-visible issues: ${newsletterIndexMeta?.visibleIssues ?? 0}; total indexed issues: ${newsletterIndexMeta?.totalIssues ?? 0}.`
                                                                : "No published Looking East issues are available yet."}
                                                        </div>
                                                    ) : (
                                                        visibleNewsletterIssues.map((issue, index) => (
                                                            <button
                                                                type="button"
                                                                key={issue.id}
                                                                className={issue.id === selectedNewsletterIssue?.id ? "active" : ""}
                                                                onClick={() => {
                                                                    setSelectedNewsletterId(issue.id);
                                                                    setSelectedNewsletterSection(null);
                                                                }}
                                                            >
                                                                <span className={`research-newsletter-issue-status ${issue.published === false ? "draft" : ""}`}>
                                                                    {issue.published === false ? "Draft" : index === 0 ? "Current issue" : formatPublicIssueLabel(issue)}
                                                                </span>
                                                                <strong>{issue.date}</strong>
                                                                <span>{formatPublicIssueLabel(issue)}</span>
                                                                <small>{issue.sections?.length || 0} contents entries</small>
                                                            </button>
                                                        ))
                                                    )}
                                                </div>

                                                <div className="research-newsletter-browser">

                                                <div className="research-newsletter-results">
                                                    {newsletterMatches.length === 0 ? (
                                                        <div className="research-empty">No contents entries matched this search.</div>
                                                    ) : (
                                                        <>
                                                            <div className="research-newsletter-results-header">
                                                                <span>Contents heading</span>
                                                                <span>Page</span>
                                                                <span>Type</span>
                                                            </div>
                                                            {newsletterMatches.map(({ issue, section }) => {
                                                                const isActive = selectedNewsletterSection?.issue?.id === issue.id &&
                                                                    selectedNewsletterSection?.section?.sourceKey === section.sourceKey;
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        className={`research-newsletter-result ${isActive ? "active" : ""}`}
                                                                        key={`${issue.id}-${section.sourceKey || section.page}-${section.title}`}
                                                                        onClick={() => openNewsletterEntry(issue, section)}
                                                                    >
                                                                        <span className="research-newsletter-title-cell">
                                                                            <strong>{section.title}</strong>
                                                                            <small>
                                                                                {newsletterSearch.trim() ? `${issue.issue} · ${issue.date}` : (section.tags || []).slice(0, 3).join(" · ")}
                                                                                {section.category === "Advert" && section.metadata?.advertType ? ` · ${section.metadata.advertType}` : ""}
                                                                                {section.category === "Advert" && section.metadata?.advertDate ? ` · ${section.metadata.advertDate}` : ""}
                                                                                {section.edited ? " · Edited" : ""}
                                                                                {section.manual ? " · Manual" : ""}
                                                                            </small>
                                                                        </span>
                                                                        <span className="research-newsletter-page">p. {section.page}</span>
                                                                        <span className="research-newsletter-category">{section.category}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </>
                                                    )}
                                                </div>

                                                {selectedNewsletterIssue && !selectedNewsletterSection && (
                                                    <section className="research-newsletter-detail cover" aria-label="Selected newsletter issue">
                                                        <div>
                                                            <p className="research-eyebrow">
                                                                {selectedNewsletterId === newsletterIssues[0]?.id ? "Current issue" : "Selected issue"}
                                                            </p>
                                                            <h3>{formatPublicIssueLabel(selectedNewsletterIssue)}</h3>
                                                            <p>
                                                                {selectedNewsletterIssue.date} · {selectedNewsletterIssue.sections?.length || 0} contents entries · {selectedNewsletterIssue.pages || 0} extracted pages
                                                            </p>
                                                            <p>
                                                                Select a contents heading to view its extracted summary here, or open the complete newsletter PDF.
                                                            </p>
                                                            {selectedNewsletterIssue.published === false && (
                                                                <p className="research-editor-error">
                                                                    Draft issue. Only archivists can see this until it is published.
                                                                </p>
                                                            )}
                                                        </div>
                                                        {getIssueCoverUrl(selectedNewsletterIssue) && (
                                                            <div className="research-newsletter-cover-card">
                                                                <img
                                                                    className="research-newsletter-cover-preview"
                                                                    src={getIssueCoverUrl(selectedNewsletterIssue)}
                                                                    alt={`${formatPublicIssueLabel(selectedNewsletterIssue)} cover page`}
                                                                    loading="lazy"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="research-newsletter-about">
                                                            <h4>About Looking East</h4>
                                                            <p>
                                                                Looking East is the newsletter of the Provincial Grand Lodge of South Africa, Northern I.C.
                                                            </p>
                                                            <dl>
                                                                <div>
                                                                    <dt>Editor</dt>
                                                                    <dd>V W Bro Francois van de Merwe</dd>
                                                                </div>
                                                                <div>
                                                                    <dt>Assistant Editor</dt>
                                                                    <dd>V W Bro Nigel Hulse</dd>
                                                                </div>
                                                                <div>
                                                                    <dt>Editorial content</dt>
                                                                    <dd>V W Bro Francois van de Merwe; V W Bro Nigel Hulse</dd>
                                                                </div>
                                                                <div>
                                                                    <dt>Email</dt>
                                                                    <dd>
                                                                        <a href="mailto:vandemer@iafrica.com">vandemer@iafrica.com</a>
                                                                    </dd>
                                                                </div>
                                                            </dl>
                                                        </div>
                                                        <div className="research-newsletter-detail-actions">
                                                            {canManageNewsletter && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateIssuePublication(selectedNewsletterIssue, selectedNewsletterIssue.published === false)}
                                                                    disabled={newsletterSaving}
                                                                >
                                                                    {selectedNewsletterIssue.published === false ? "Publish Issue" : "Move to Draft"}
                                                                </button>
                                                            )}
                                                            <a
                                                                className="primary"
                                                                href={getIssuePdfUrl(selectedNewsletterIssue)}
                                                                onClick={() => logIssuePdfOpen(selectedNewsletterIssue)}
                                                            >
                                                                Open Full PDF
                                                            </a>
                                                        </div>
                                                    </section>
                                                )}

                                                {selectedNewsletterSection && (
                                                    <section
                                                        className="research-newsletter-detail"
                                                        aria-label="Selected newsletter section"
                                                        ref={selectedNewsletterDetailRef}
                                                        tabIndex="-1"
                                                    >
                                                        <div>
                                                            <p className="research-eyebrow">
                                                                {selectedNewsletterSection.issue.issue} · {selectedNewsletterSection.issue.date} · Page {selectedNewsletterSection.section.page}
                                                            </p>
                                                            <h3>{selectedNewsletterSection.section.title}</h3>
                                                            <p>{selectedNewsletterSection.section.snippet}</p>
                                                            <div className="research-chip-row">
                                                                {selectedNewsletterSection.section.category === "Advert" && selectedNewsletterSection.section.metadata?.advertType && (
                                                                    <span>{selectedNewsletterSection.section.metadata.advertType}</span>
                                                                )}
                                                                {selectedNewsletterSection.section.category === "Advert" && selectedNewsletterSection.section.metadata?.advertDate && (
                                                                    <span>{selectedNewsletterSection.section.metadata.advertDate}</span>
                                                                )}
                                                                {(selectedNewsletterSection.section.tags || []).map((tag) => (
                                                                    <span key={tag}>{tag}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="research-newsletter-detail-actions">
                                                            {canManageNewsletter && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openNewsletterEditor("edit", selectedNewsletterSection.issue, selectedNewsletterSection.section)}
                                                                    >
                                                                        Edit Entry
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="danger"
                                                                        onClick={() => hideNewsletterSection(selectedNewsletterSection.issue, selectedNewsletterSection.section)}
                                                                        disabled={newsletterSaving}
                                                                    >
                                                                        Hide Entry
                                                                    </button>
                                                                </>
                                                            )}
                                                            <a
                                                                href={getIssuePdfUrl(selectedNewsletterSection.issue)}
                                                                onClick={() => logIssuePdfOpen(selectedNewsletterSection.issue)}
                                                            >
                                                                Open Full PDF
                                                            </a>
                                                            <a
                                                                className="primary"
                                                                href={getIssuePdfUrl(selectedNewsletterSection.issue, selectedNewsletterSection.section.page)}
                                                                onClick={() => logIssuePdfOpen(selectedNewsletterSection.issue, selectedNewsletterSection.section.page)}
                                                            >
                                                                Open Page
                                                            </a>
                                                        </div>
                                                        {newsletterEditorError && <div className="research-editor-error">{newsletterEditorError}</div>}
                                                    </section>
                                                )}
                                                </div>
                                            </>
                                        )}

                                        {canManageNewsletter && newsletterEditor && (
                                            <div
                                                className="research-editor-modal-backdrop"
                                                role="dialog"
                                                aria-modal="true"
                                                aria-label={newsletterEditor.mode === "add" ? "Add newsletter contents entry" : "Edit newsletter contents entry"}
                                                onMouseDown={() => setNewsletterEditor(null)}
                                            >
                                                <form
                                                    className="research-newsletter-editor research-editor-modal"
                                                    onSubmit={saveNewsletterEditor}
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                >
                                                    <div className="research-editor-header">
                                                        <div>
                                                            <p className="research-eyebrow">Editorial tools</p>
                                                            <h3>{newsletterEditor.mode === "add" ? "Add contents entry" : "Edit contents entry"}</h3>
                                                        </div>
                                                        <button type="button" onClick={() => setNewsletterEditor(null)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                    <label>
                                                        Title
                                                        <input
                                                            value={newsletterEditor.title}
                                                            onChange={(event) => setNewsletterEditor((current) => ({ ...current, title: event.target.value }))}
                                                            required
                                                        />
                                                    </label>
                                                    <div className="research-editor-grid">
                                                        <label>
                                                            Page to open
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={newsletterEditor.page}
                                                                onChange={(event) => setNewsletterEditor((current) => ({ ...current, page: event.target.value }))}
                                                                required
                                                            />
                                                        </label>
                                                        <label>
                                                            Printed page
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={newsletterEditor.printedPage}
                                                                onChange={(event) => setNewsletterEditor((current) => ({ ...current, printedPage: event.target.value }))}
                                                            />
                                                        </label>
                                                        <label>
                                                            Category
                                                            <select
                                                                value={newsletterEditor.category}
                                                                onChange={(event) => setNewsletterEditor((current) => ({ ...current, category: event.target.value }))}
                                                            >
                                                                {newsletterCategories.map((category) => (
                                                                    <option key={category} value={category}>{category}</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                        <label>
                                                            Tags
                                                            <input
                                                                value={newsletterEditor.tags}
                                                                onChange={(event) => setNewsletterEditor((current) => ({ ...current, tags: event.target.value }))}
                                                                placeholder="PGL, charity, installation"
                                                            />
                                                        </label>
                                                    </div>
                                                    {newsletterEditor.category === "Advert" && (
                                                        <div className="research-editor-grid">
                                                            <label>
                                                                Advert type
                                                                <select
                                                                    value={newsletterEditor.advertType}
                                                                    onChange={(event) => setNewsletterEditor((current) => ({ ...current, advertType: event.target.value }))}
                                                                >
                                                                    {advertTypes.map((type) => (
                                                                        <option key={type} value={type}>{type}</option>
                                                                    ))}
                                                                </select>
                                                            </label>
                                                            <label>
                                                                Relevant date
                                                                <input
                                                                    type="date"
                                                                    value={newsletterEditor.advertDate || ""}
                                                                    onChange={(event) => setNewsletterEditor((current) => ({ ...current, advertDate: event.target.value }))}
                                                                />
                                                            </label>
                                                        </div>
                                                    )}
                                                    <label>
                                                        Summary / snippet
                                                        <textarea
                                                            value={newsletterEditor.snippet}
                                                            onChange={(event) => setNewsletterEditor((current) => ({ ...current, snippet: event.target.value }))}
                                                            rows={4}
                                                        />
                                                    </label>
                                                    {newsletterEditorError && <div className="research-editor-error">{newsletterEditorError}</div>}
                                                    <div className="research-newsletter-detail-actions">
                                                        <button type="button" onClick={() => setNewsletterEditor(null)}>
                                                            Cancel
                                                        </button>
                                                        <button type="submit" className="primary" disabled={newsletterSaving}>
                                                            {newsletterSaving ? "Saving..." : "Save Entry"}
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        )}

                                        <div className="research-newsletter-next">
                                            <strong>Refreshing after archive edits</strong>
                                            <span>After editing newsletter metadata or uploading a new Looking East PDF, click Refresh Index. It calls the backend with refresh=true, clears the cache, re-reads the database, and re-extracts PDF contents.</span>
                                            <span>A backend restart also refreshes it automatically, but the button is faster for day-to-day archive work.</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="research-detail-actions">
                                <button type="button" onClick={() => setSelectedTopic(null)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {renderPublicationQueueModal()}
            {publicationQueuePreview.open && (
                <PreviewModal
                    title={publicationQueuePreview.title}
                    previewContent={publicationQueuePreview.content}
                    previewType={publicationQueuePreview.type}
                    previewError={publicationQueuePreview.error}
                    downloadUrl={publicationQueuePreview.downloadUrl}
                    onClose={() => setPublicationQueuePreview({
                        open: false,
                        title: "",
                        content: null,
                        type: null,
                        error: "",
                        downloadUrl: "",
                    })}
                />
            )}
            {publicImagePreview.open && (
                <PreviewModal
                    title={publicImagePreview.title}
                    previewContent={publicImagePreview.content}
                    previewType="image"
                    previewError=""
                    downloadUrl={publicImagePreview.downloadUrl}
                    onClose={() => setPublicImagePreview({
                        open: false,
                        title: "",
                        content: null,
                        downloadUrl: "",
                    })}
                />
            )}
        </main>
    );
}

export default ResearchPage;
